import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sun, Upload, FileText, Mic, Square, CheckCircle2, Bell, Ban, CalendarClock, Trash2, Loader2, PlusCircle, History, Calendar, Link2, RefreshCw, Video, MapPin, MessageCircle, Settings, Database, DownloadCloud, UploadCloud, FilePen, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Search, X, UserX, Mic2, UserCog, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CommissionsPanel } from "@/components/CommissionsPanel";
import { italianTimeToLocal, isBrowserInItaly, getLocalTzLabel, hoursOffsetFromItaly } from "@/lib/tz";

// Formatta un orario italiano HH:MM aggiungendo (se browser in fuso ≠ Italia) l'ora locale
// Ritorna JSX pronto da inserire. Compatto per badge inline.
const ItalyTimeInline = ({ dateStr, hhmm, size = "sm" }) => {
  if (!hhmm) return null;
  const local = italianTimeToLocal(dateStr, hhmm);
  const cls = size === "xs" ? "text-[9px]" : "text-[10px]";
  return (
    <span className="inline-flex items-center gap-1">
      <span>🇮🇹 {hhmm}</span>
      {local && (
        <span className={`${cls} text-[#0A0A0A]/50 normal-case tracking-normal`}>· tu {local}</span>
      )}
    </span>
  );
};

// Banner informativo mostrato solo se il browser NON è in fuso italiano.
// Ricorda all'operatore che tutti gli orari mostrati sono in ora italiana.
const TzBanner = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  if (isBrowserInItaly()) return null;
  const label = getLocalTzLabel() || "locale";
  const italyNow = now.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });
  const localNow = now.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Rome" }).format(now);
  const offset = hoursOffsetFromItaly(today);
  const sign = offset > 0 ? `+${offset}h` : `${offset}h`;
  return (
    <div
      className="mt-3 flex items-center gap-2 flex-wrap text-xs border-2 border-[#FF5A1F] bg-[#FFF5EF] px-3 py-2"
      data-testid="tz-banner"
      title="Tutti gli orari degli appuntamenti sono in ora italiana. Sotto vedi la conversione nel tuo fuso."
    >
      <span className="font-bold uppercase tracking-widest text-[#FF5A1F]">Fuso orario</span>
      <span>🇮🇹 Italia <b>{italyNow}</b></span>
      <span className="text-[#0A0A0A]/50">·</span>
      <span>📍 {label} <b>{localNow}</b> <span className="text-[#0A0A0A]/50">({sign})</span></span>
      <span className="text-[#0A0A0A]/60 ml-1">Gli appuntamenti sono mostrati in ora italiana.</span>
    </div>
  );
};

const STATUS_META = {
  non_fissata: { label: "1ª call da fissare", icon: CalendarClock, cls: "bg-slate-500 text-white border-slate-600" },
  signed:    { label: "Contratto firmato", icon: CheckCircle2, cls: "bg-green-600 text-white border-green-700" },
  attesa_firma: { label: "Attesa firma",    icon: Clock,         cls: "bg-emerald-600 text-white border-emerald-700" },
  da_contattare: { label: "Da contattare noi", icon: Bell,       cls: "bg-cyan-600 text-white border-cyan-700" },
  call_back: { label: "2ª call",            icon: Bell,          cls: "bg-[#FF5A1F] text-white border-[#FF5A1F]" },
  no_call:   { label: "Non richiamare",    icon: Ban,           cls: "bg-[#0A0A0A] text-white border-[#0A0A0A]" },
  next_call: { label: "Altra call",        icon: CalendarClock, cls: "bg-blue-600 text-white border-blue-700" },
  no_show:   { label: "No show",           icon: UserX,         cls: "bg-red-600 text-white border-red-700" },
  rinuncia:  { label: "Rinuncia",          icon: UserX,         cls: "bg-amber-700 text-white border-amber-800" },
  rimanda:   { label: "Rimanda",           icon: CalendarClock, cls: "bg-indigo-600 text-white border-indigo-700" },
  scomparso: { label: "Scomparso",         icon: Ban,           cls: "bg-rose-700 text-white border-rose-800" },
};

const RIMANDA_REASONS = [
  { value: "finanziario", label: "Finanziario" },
  { value: "medico",      label: "Medico" },
  { value: "famiglia",    label: "Famiglia" },
  { value: "lavoro",      label: "Lavoro" },
  { value: "altro",       label: "Altro" },
];

const SCOMPARSO_REASONS = [
  { value: "prima_call",   label: "Scomparso dopo 1ª call" },
  { value: "seconda_call", label: "Scomparso dopo 2ª call" },
];

const REASON_LABEL = (status, reason) => {
  if (!reason) return "";
  if (status === "rimanda") {
    const r = RIMANDA_REASONS.find((x) => x.value === reason);
    return r ? r.label : reason;
  }
  if (status === "scomparso") {
    const r = SCOMPARSO_REASONS.find((x) => x.value === reason);
    return r ? r.label : reason;
  }
  return "";
};

// -----------------------------------------------------------------------------
// WhatsApp helpers (semi-automatic: pre-compiled wa.me links)
// -----------------------------------------------------------------------------
const DEFAULT_WA_TEMPLATES = {
  morning: `Ciao {name},
sono {sender} della Perla Music. Ti ricordo che oggi alle {time} (ora italiana 🇮🇹) abbiamo la nostra call.
15 minuti prima ti mando un altro messaggio con il link per collegarti.
A dopo!`,
  precall: `Ciao {name}!
Ecco il link per la nostra call che parte fra pochi minuti:
{link}
Ci vediamo tra un attimo,
{sender} — Perla Music`,
};

const loadTemplates = () => {
  try {
    const raw = localStorage.getItem("perla.waTemplates");
    if (raw) return { ...DEFAULT_WA_TEMPLATES, ...JSON.parse(raw) };
  } catch (e) {}
  return { ...DEFAULT_WA_TEMPLATES };
};
const saveTemplates = (t) => {
  try { localStorage.setItem("perla.waTemplates", JSON.stringify(t)); } catch (e) {}
};

// Extract artist name from event summary like "La Perla Music (Mario Rossi)"
const cleanArtistName = (summary) => {
  if (!summary) return "";
  const m = summary.match(/\(([^)]+)\)/);
  if (m) return m[1].trim();
  return summary.replace(/^la\s+perla\s+music/i, "").replace(/[-–—:|]/g, "").trim() || summary;
};

// Normalize phone to digits only + country prefix (defaults to +39 Italy)
const normalizePhone = (raw) => {
  if (!raw) return "";
  let p = String(raw).replace(/[^\d+]/g, "");
  if (p.startsWith("+")) p = p.slice(1);
  if (!p) return "";
  if (p.startsWith("00")) p = p.slice(2);
  // If it looks like an Italian mobile without prefix (starts with 3, ≤10 digits), add 39
  if (p.length <= 10 && /^3/.test(p)) p = "39" + p;
  return p;
};

const buildWaLink = (phone, text) => {
  const p = normalizePhone(phone);
  if (!p) return null;
  return `https://wa.me/${p}?text=${encodeURIComponent(text)}`;
};

// Risolve il numero corretto per un evento Calendar.
// Priorità:
//   1. phone estratto dalla descrizione dell'evento (ev.phone) — se presente vince
//   2. attendee che "corrisponde" all'artist name del summary (matching su email o rubrica name)
//   3. se c'è UN SOLO attendee (dopo aver escluso me stesso), usa quello
//   4. altrimenti nessun match → non tirare a indovinare (meglio disattivare il bottone WA)
const resolveEventPhone = (ev, rubrica, rubricaByEmail, myEmail, artistName) => {
  if (ev?.phone) return ev.phone;
  const myEmailLc = (myEmail || "").toLowerCase();
  const attendees = (ev?.attendees || [])
    .map((a) => (typeof a === "string" ? a : a?.email))
    .filter((em) => em && em.toLowerCase() !== myEmailLc);
  if (attendees.length === 0) return "";

  // Normalizza artistName per confronto
  const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const nArtist = norm(artistName);

  // 2) Match forte per artist name
  if (nArtist) {
    for (const em of attendees) {
      const local = em.split("@")[0].toLowerCase();
      if (norm(local).includes(nArtist) || nArtist.includes(norm(local))) {
        const p = rubricaByEmail[em.toLowerCase()];
        if (p) return p;
      }
    }
    // Match via rubrica name -> email
    for (const c of rubrica || []) {
      if (!c.email || !c.phone) continue;
      if (norm(c.name).includes(nArtist) || nArtist.includes(norm(c.name))) {
        if (attendees.some((em) => em.toLowerCase() === c.email.toLowerCase())) {
          return c.phone;
        }
      }
    }
  }

  // 3) Un solo attendee reale → usalo (comportamento pre-esistente ma sicuro)
  if (attendees.length === 1) {
    return rubricaByEmail[attendees[0].toLowerCase()] || "";
  }

  // 4) Ambiguo: NON scegliere il primo per non aprire WA sul numero sbagliato
  return "";
};

const fillTemplate = (tpl, vars) =>
  Object.entries(vars).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, v ?? ""), tpl);

const todayISO = () => {
  // Local calendar date (not UTC) — avoids timezone shift bugs
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const addDays = (isoDate, n) => {
  // Parse & operate in UTC so setDate arithmetic doesn't cross DST boundaries,
  // then format back as YYYY-MM-DD from UTC parts (still a pure calendar date).
  const [y, m, d] = isoDate.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};
const formatDayHeader = (isoDate) => {
  const [y, m, d] = isoDate.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", timeZone: "UTC" });
};

function ContactSearchBar() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get(`/mio-giorno/search?q=${encodeURIComponent(q.trim())}`);
        setResults(r.data || []);
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);
  }, [q]);

  const jumpToCard = (card) => {
    setOpen(false); setQ("");
    // Cambia data corrente + scrolla alla card dopo un tick
    const evt = new CustomEvent("mg-jump-to-card", { detail: { date: card.date, cardId: card.id } });
    window.dispatchEvent(evt);
  };

  return (
    <div className="relative">
      <div className="flex items-center border-2 border-[#0A0A0A] bg-[#FAFAF7] focus-within:border-[#FF5A1F]">
        <Search size={16} className="ml-3 text-[#0A0A0A]/40" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => q.trim().length >= 2 && setOpen(true)}
          placeholder="Cerca contatto in agenda (nome, email, telefono, note...)"
          className="flex-1 px-3 py-2.5 text-sm bg-transparent focus:outline-none"
          data-testid="agenda-search-input"
        />
        {q && (
          <button onClick={() => { setQ(""); setResults([]); setOpen(false); }} className="mr-2 p-1 text-[#0A0A0A]/40 hover:text-[#FF5A1F]" data-testid="agenda-search-clear">
            <X size={14} />
          </button>
        )}
        {loading && <Loader2 size={14} className="mr-3 animate-spin text-[#FF5A1F]" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 z-30 max-h-96 overflow-auto border-2 border-[#0A0A0A] bg-white shadow-xl" data-testid="agenda-search-results">
          {results.map((c) => (
            <button
              key={c.id}
              onClick={() => jumpToCard(c)}
              className="w-full text-left px-4 py-3 border-b border-[#0A0A0A]/10 hover:bg-[#F2F2EC] flex items-center justify-between gap-3 group"
              data-testid={`agenda-search-result-${c.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-display text-sm uppercase text-[#0A0A0A] truncate">{c.artist_name}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/50 mt-0.5">
                  {new Date(c.date).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short", year: "numeric" })}
                  {c.status && STATUS_META[c.status] && (
                    <span className={`ml-2 inline-block px-1.5 py-0.5 text-[9px] ${STATUS_META[c.status].cls}`}>
                      {STATUS_META[c.status].label}
                      {c.status_reason && (c.status === "rimanda" || c.status === "scomparso") && (
                        <span className="ml-1 opacity-80">· {REASON_LABEL(c.status, c.status_reason)}</span>
                      )}
                    </span>
                  )}
                </div>
              </div>
              <ChevronRight size={14} className="text-[#0A0A0A]/30 group-hover:text-[#FF5A1F]" />
            </button>
          ))}
        </div>
      )}
      {open && results.length === 0 && !loading && q.trim().length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-1 z-30 border-2 border-[#0A0A0A] bg-white px-4 py-3 text-sm text-[#0A0A0A]/60">
          Nessun contatto trovato per "{q}"
        </div>
      )}
    </div>
  );
}

export default function AdminMioGiorno() {
  const [startDate, setStartDate] = useState(todayISO());
  const [daysRange, setDaysRange] = useState(1); // 1 / 3 / 7
  const [daysData, setDaysData] = useState([]);   // [{date, day, cards}]
  const [loading, setLoading] = useState(false);
  const [contractsIndex, setContractsIndex] = useState({}); // lowercase(name) -> contract

  const dateList = useMemo(
    () => Array.from({ length: daysRange }, (_, i) => addDays(startDate, i)),
    [startDate, daysRange]
  );

  const loadRange = async () => {
    setLoading(true);
    try {
      const results = await Promise.all(dateList.map((d) => api.get(`/mio-giorno/day/${d}`)));
      setDaysData(
        dateList.map((d, i) => ({
          date: d,
          day: results[i].data.day,
          cards: results[i].data.cards || [],
        }))
      );
    } catch (e) {
      toast.error("Errore caricamento");
    } finally {
      setLoading(false);
    }
  };

  const loadContractsIndex = async () => {
    try {
      const r = await api.get("/contratti");
      const idx = {};
      (r.data || []).forEach((c) => {
        const key = (c.artist_name || "").trim().toLowerCase();
        if (key && !idx[key]) idx[key] = c;
      });
      setContractsIndex(idx);
    } catch { /* silent */ }
  };

  useEffect(() => { loadRange(); /* eslint-disable-next-line */ }, [startDate, daysRange]);
  useEffect(() => { loadContractsIndex(); }, []);

  // NOTA: l'auto-backfill è stato DISABILITATO perché il matching cross-artista
  // via email condivisa (es. nnd@gmail.com placeholder di Google Calendar)
  // aveva causato pollution: schede di artisti diversi ricevevano dati errati.
  // Il backfill ora si triggera SOLO dal bottone manuale "Recupera dati" e
  // opera con matching molto più conservativo (name + phone/email).

  // Backfill manuale on-demand (bottone "Recupera dati mancanti")
  const runBackfill = async () => {
    if (!window.confirm(
      "Il recupero automatico cerca schede vuote e copia dati dalle schede storiche dello stesso cliente (nome simile + telefono). \n\n" +
      "⚠️ Se hai molti clienti che condividono email/telefono generici (placeholder), preferisci usare 'Unisci con...' manualmente su ciascuna scheda.\n\n" +
      "Vuoi procedere con il recupero?"
    )) return;
    try {
      const r = await api.post("/mio-giorno/enrich-empty-cards");
      const n = r.data?.enriched_cards || 0;
      const f = r.data?.fields_added || 0;
      if (n === 0) toast.info("Nessuna scheda vuota da recuperare — tutti i dati sono già collegati");
      else toast.success(`Recuperati dati su ${n} schede (${f} campi aggiunti)`);
      window.dispatchEvent(new CustomEvent("mg-refresh"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante il recupero");
    }
  };

  // Ripristina schede inquinate dal bug pollution
  const [pollutedModal, setPollutedModal] = useState(null); // { items: [], selected: Set }

  const runRestorePolluted = async () => {
    try {
      const check = await api.get("/mio-giorno/polluted-cards");
      const items = check.data?.items || [];
      if (items.length === 0) {
        toast.success("Nessuna scheda inquinata trovata — tutto pulito");
        return;
      }
      // Apri modale con tutti gli item, tutti selezionati di default
      setPollutedModal({
        items,
        selected: new Set(items.map((p) => p.card_id)),
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante il controllo");
    }
  };

  const confirmRestorePolluted = async () => {
    if (!pollutedModal) return;
    const selected = Array.from(pollutedModal.selected);
    if (selected.length === 0) {
      toast.info("Nessuna scheda selezionata");
      return;
    }
    try {
      const r = await api.post("/mio-giorno/restore-polluted-bulk", { card_ids: selected });
      const n = r.data?.restored_cards || 0;
      const f = r.data?.removed_fields_total || 0;
      toast.success(`Ripristinate ${n} schede — rimossi ${f} campi copiati per errore`);
      setPollutedModal(null);
      window.dispatchEvent(new CustomEvent("mg-refresh"));
      loadRange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante il ripristino");
    }
  };

  // Listen for search "jump-to-card": change date + scroll
  useEffect(() => {
    const onJump = (e) => {
      const { date, cardId } = e.detail || {};
      if (!date || !cardId) return;
      // Set start date to the card's date (single-day view if search jumps far)
      setStartDate(date);
      setDaysRange(1);
      // Scroll to card after next render
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="mio-giorno-card-${cardId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-4", "ring-[#FF5A1F]");
          setTimeout(() => el.classList.remove("ring-4", "ring-[#FF5A1F]"), 2200);
        }
      }, 900);
    };
    window.addEventListener("mg-jump-to-card", onJump);
    const onRefresh = () => { loadRange(); loadContractsIndex(); };
    window.addEventListener("mg-refresh", onRefresh);
    return () => {
      window.removeEventListener("mg-jump-to-card", onJump);
      window.removeEventListener("mg-refresh", onRefresh);
    };
  }, [startDate, daysRange]);

  const patchDay = (date, patch) =>
    setDaysData((ds) => ds.map((d) => (d.date === date ? { ...d, ...patch } : d)));

  const handleUpload = async (date, file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    form.append("date", date);
    try {
      const res = await api.post("/mio-giorno/upload-pdf", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Estratte ${res.data.cards_count} schede per ${date}`);
      await loadRange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fallito");
    }
  };

  const addManualCard = async (date, name) => {
    if (!name.trim()) return;
    try {
      await api.post("/mio-giorno/card", { date, artist_name: name.trim() });
      toast.success("Scheda aggiunta");
      loadRange();
    } catch (e) {
      toast.error("Errore");
    }
  };

  const deleteCard = async (date, cardId) => {
    if (!window.confirm("Eliminare questa scheda?")) return;
    try {
      await api.delete(`/mio-giorno/card/${cardId}`);
      patchDay(date, { cards: (daysData.find((d) => d.date === date)?.cards || []).filter((c) => c.id !== cardId) });
    } catch (e) {
      toast.error("Errore");
    }
  };

  const setStatus = async (date, cardId, status, callBackDate = null, extra = {}) => {
    try {
      await api.patch(`/mio-giorno/card/${cardId}/status`, {
        status,
        call_back_date: callBackDate,
        status_reason: extra.status_reason || null,
        status_note: extra.status_note || null,
        no_show_recovered_to: extra.no_show_recovered_to || null,
        scomparso_recovered_to: extra.scomparso_recovered_to || null,
      });
      const cards = (daysData.find((d) => d.date === date)?.cards || []).map((c) =>
        c.id === cardId
          ? {
              ...c,
              status,
              call_back_date: callBackDate,
              status_reason: extra.status_reason || null,
              status_note: extra.status_note || null,
              no_show_recovered_to: extra.no_show_recovered_to || null,
              scomparso_recovered_to: extra.scomparso_recovered_to || null,
            }
          : c
      );
      patchDay(date, { cards });
    } catch (e) {
      toast.error("Errore aggiornamento status");
    }
  };

  const pushNote = (date, cardId, note) => {
    const cards = (daysData.find((d) => d.date === date)?.cards || []).map((c) =>
      c.id === cardId ? { ...c, notes: [...(c.notes || []), note] } : c
    );
    patchDay(date, { cards });
  };
  const removeNote = (date, cardId, noteId) => {
    const cards = (daysData.find((d) => d.date === date)?.cards || []).map((c) =>
      c.id === cardId ? { ...c, notes: (c.notes || []).filter((n) => n.id !== noteId) } : c
    );
    patchDay(date, { cards });
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="mio-giorno-page">
      <header className="flex flex-col gap-4 mb-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#FF5A1F]">
              <Sun size={14} /> Admin Privato
            </div>
            <h1 className="mt-2 font-display text-4xl md:text-5xl uppercase text-[#0A0A0A]">Il mio giorno</h1>
            <p className="mt-2 text-sm text-[#0A0A0A]/60 max-w-xl">Agenda + schede lead, ordinate cronologicamente. Cerca un contatto o naviga giorno per giorno con le frecce.</p>
            <UnreadLeadsPill />
            <TzBanner />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <BackupRestoreButton />
            <div className="flex items-center gap-1">
              <button
                onClick={() => setStartDate(addDays(startDate, -daysRange))}
                className="border-2 border-[#0A0A0A] px-2.5 py-2 hover:bg-[#FF5A1F] hover:border-[#FF5A1F] hover:text-white transition-colors"
                title={`Indietro di ${daysRange} giorn${daysRange === 1 ? "o" : "i"}`}
                data-testid="date-prev-btn"
              >
                <ChevronLeft size={16} />
              </button>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border-2 border-[#0A0A0A] px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#FF5A1F]"
                data-testid="mio-giorno-date-input"
              />
              <button
                onClick={() => setStartDate(addDays(startDate, daysRange))}
                className="border-2 border-[#0A0A0A] px-2.5 py-2 hover:bg-[#FF5A1F] hover:border-[#FF5A1F] hover:text-white transition-colors"
                title={`Avanti di ${daysRange} giorn${daysRange === 1 ? "o" : "i"}`}
                data-testid="date-next-btn"
              >
                <ChevronRight size={16} />
              </button>
              {startDate !== todayISO() && (
                <button
                  onClick={() => setStartDate(todayISO())}
                  className="ml-1 border-2 border-[#0A0A0A]/30 hover:border-[#FF5A1F] hover:text-[#FF5A1F] px-3 py-2 text-[10px] uppercase tracking-widest font-bold"
                  title="Torna a oggi"
                  data-testid="date-today-btn"
                >
                  Oggi
                </button>
              )}
              <button
                onClick={runBackfill}
                className="ml-1 border-2 border-[#FF5A1F] bg-[#FFF5EF] text-[#FF5A1F] hover:bg-[#FF5A1F] hover:text-white transition-colors px-3 py-2 text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1"
                title="Cerca schede vuote e recupera automaticamente i dati storici (nome simile, stesso telefono o email)"
                data-testid="enrich-empty-cards-btn"
              >
                <RefreshCw size={12} /> Recupera dati
              </button>
              <button
                onClick={runRestorePolluted}
                className="ml-1 border-2 border-red-500 bg-red-50 text-red-700 hover:bg-red-500 hover:text-white transition-colors px-3 py-2 text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1"
                title="Ripristina schede inquinate da un merge errato del passato (rimuove i campi copiati da schede di altri artisti)"
                data-testid="restore-polluted-btn"
              >
                <UserX size={12} /> Ripulisci schede
              </button>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs uppercase tracking-widest text-[#0A0A0A]/60">Vista</label>
              <select
                value={daysRange}
                onChange={(e) => setDaysRange(parseInt(e.target.value))}
                className="border-2 border-[#0A0A0A] px-3 py-2 text-sm font-mono bg-[#FAFAF7] focus:outline-none focus:border-[#FF5A1F]"
                data-testid="mio-giorno-range-select"
              >
                <option value={1}>Solo oggi</option>
                <option value={3}>3 giorni</option>
                <option value={7}>7 giorni</option>
              </select>
            </div>
          </div>
        </div>
        {/* Ricerca contatto trasversale */}
        <ContactSearchBar />
      </header>
      <CommissionsPanel scope="vendor" title="Le mie provvigioni" />

      {pollutedModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="polluted-modal">
          <div className="bg-[#FAFAF7] border-2 border-red-500 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="border-b-2 border-red-500 p-4 sticky top-0 bg-[#FAFAF7] z-10">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display text-2xl uppercase text-red-700">Ripulisci schede inquinate</div>
                  <div className="text-xs text-[#0A0A0A]/70 mt-1">
                    Trovate <b>{pollutedModal.items.length}</b> schede con campi copiati da <b>artisti diversi</b>.
                    Deseleziona quelle che vuoi tenere così come sono.
                  </div>
                </div>
                <button onClick={() => setPollutedModal(null)} className="p-2 hover:text-red-600" data-testid="polluted-close">
                  <X size={18} />
                </button>
              </div>
              <div className="mt-3 flex items-center gap-3 text-xs">
                <button
                  onClick={() => setPollutedModal({ ...pollutedModal, selected: new Set(pollutedModal.items.map((p) => p.card_id)) })}
                  className="border border-[#0A0A0A]/30 px-2 py-1 hover:border-[#0A0A0A]"
                  data-testid="polluted-select-all"
                >Seleziona tutte</button>
                <button
                  onClick={() => setPollutedModal({ ...pollutedModal, selected: new Set() })}
                  className="border border-[#0A0A0A]/30 px-2 py-1 hover:border-[#0A0A0A]"
                  data-testid="polluted-select-none"
                >Deseleziona tutte</button>
                <span className="text-[#0A0A0A]/50 ml-auto">{pollutedModal.selected.size} / {pollutedModal.items.length} selezionate</span>
              </div>
            </div>

            <div className="p-4 space-y-2">
              {pollutedModal.items.map((p) => {
                const checked = pollutedModal.selected.has(p.card_id);
                return (
                  <label
                    key={p.card_id}
                    className={`block border-2 p-3 cursor-pointer transition-colors ${checked ? "border-red-500 bg-red-50" : "border-[#0A0A0A]/20 bg-white"}`}
                    data-testid={`polluted-item-${p.card_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const next = new Set(pollutedModal.selected);
                          if (next.has(p.card_id)) next.delete(p.card_id); else next.add(p.card_id);
                          setPollutedModal({ ...pollutedModal, selected: next });
                        }}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-sm">{p.card_artist}</span>
                          <span className="text-[10px] text-[#0A0A0A]/60">{p.card_date}</span>
                        </div>
                        <div className="text-[11px] text-red-700 mt-0.5">
                          <b>{p.shared_field_count} campi identici</b> a "<b>{p.parent_artist}</b>" → probabile copia errata
                        </div>
                        {p.shared_field_keys && p.shared_field_keys.length > 0 && (
                          <div className="text-[10px] text-[#0A0A0A]/50 mt-1 truncate">
                            Campi da rimuovere: {p.shared_field_keys.slice(0, 6).join(", ")}
                            {p.shared_field_keys.length > 6 && ` +${p.shared_field_keys.length - 6} altri`}
                          </div>
                        )}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            <div className="border-t-2 border-red-500 p-4 flex items-center justify-between sticky bottom-0 bg-[#FAFAF7]">
              <div className="text-[11px] text-[#0A0A0A]/70">
                Verranno preservati: <b>telefono, email, nome, orario, status, note vocali</b>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPollutedModal(null)}
                  className="px-4 py-2 border-2 border-[#0A0A0A]/30 text-sm uppercase tracking-widest font-bold hover:border-[#0A0A0A]"
                  data-testid="polluted-cancel"
                >Annulla</button>
                <button
                  onClick={confirmRestorePolluted}
                  disabled={pollutedModal.selected.size === 0}
                  className="px-4 py-2 border-2 border-red-500 bg-red-500 text-white text-sm uppercase tracking-widest font-bold hover:bg-red-700 hover:border-red-700 disabled:opacity-50 inline-flex items-center gap-1"
                  data-testid="polluted-confirm"
                >
                  <Trash2 size={14} /> Ripulisci selezionate ({pollutedModal.selected.size})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Google Calendar section (uses same range) */}
      <CalendarSection startDate={startDate} days={daysRange} />

      {/* Days list */}
      {loading && daysData.length === 0 && (
        <div className="border-2 border-dashed border-[#0A0A0A]/30 p-8 text-center text-[#0A0A0A]/50 text-sm flex items-center justify-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Caricamento schede...
        </div>
      )}

      <div className="space-y-10">
        {daysData.map((d) => (
          <DaySection
            key={d.date}
            date={d.date}
            day={d.day}
            cards={d.cards}
            contractsIndex={contractsIndex}
            onUpload={(file) => handleUpload(d.date, file)}
            onAddManual={(name) => addManualCard(d.date, name)}
            onDelete={(cardId) => deleteCard(d.date, cardId)}
            onStatus={(cardId, s, cb, extra) => setStatus(d.date, cardId, s, cb, extra)}
            onNoteAdded={(cardId, n) => pushNote(d.date, cardId, n)}
            onNoteRemoved={(cardId, nid) => removeNote(d.date, cardId, nid)}
          />
        ))}
      </div>
    </div>
  );
}

function DaySection({ date, day, cards, contractsIndex, onUpload, onAddManual, onDelete, onStatus, onNoteAdded, onNoteRemoved }) {
  const [uploading, setUploading] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualName, setManualName] = useState("");

  const isToday = date === todayISO();
  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try { await onUpload(file); } finally { setUploading(false); }
  };
  const doAdd = async () => {
    if (!manualName.trim()) return;
    await onAddManual(manualName);
    setManualName("");
    setShowManual(false);
  };

  return (
    <section className="border-t-4 border-[#0A0A0A] pt-6" data-testid={`day-section-${date}`}>
      {/* Day header */}
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-baseline gap-3">
          <h2 className="font-display text-3xl uppercase text-[#0A0A0A]">{formatDayHeader(date)}</h2>
          {isToday && <span className="text-[10px] uppercase tracking-widest text-white bg-[#FF5A1F] px-2 py-0.5">Oggi</span>}
        </div>
        <div className="text-xs uppercase tracking-widest text-[#0A0A0A]/70" data-testid={`day-count-${date}`}>
          {cards.length} {cards.length === 1 ? "scheda" : "schede"}
        </div>
      </div>

      {/* Upload PDF */}
      <div className="border-2 border-[#0A0A0A] p-4 bg-[#F2F2EC] mb-4 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid={`day-upload-${date}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 border-2 border-[#0A0A0A] bg-[#FAFAF7] flex items-center justify-center">
            <FileText size={18} />
          </div>
          <div className="text-xs text-[#0A0A0A]/70">
            {day?.pdf_filename ? <>PDF caricato: <b>{day.pdf_filename}</b></> : "Nessun PDF caricato per questo giorno"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowManual((v) => !v)}
            className="text-xs uppercase tracking-widest text-[#FF5A1F] flex items-center gap-1 hover:underline"
            data-testid={`day-manual-toggle-${date}`}
          >
            <PlusCircle size={14} /> Aggiungi manualmente
          </button>
          <label className={`btn-cta cursor-pointer text-xs ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid={`day-upload-btn-${date}`}>
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "Estrazione..." : "Carica PDF"}
            <input
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => doUpload(e.target.files?.[0])}
              data-testid={`day-file-input-${date}`}
            />
          </label>
        </div>
      </div>

      {showManual && (
        <div className="border-2 border-dashed border-[#0A0A0A] p-3 mb-4 flex gap-2" data-testid={`day-manual-form-${date}`}>
          <input
            type="text"
            placeholder="Nome artista"
            value={manualName}
            onChange={(e) => setManualName(e.target.value)}
            className="flex-1 border-2 border-[#0A0A0A] px-3 py-2 text-sm focus:outline-none focus:border-[#FF5A1F]"
            data-testid={`day-manual-input-${date}`}
          />
          <button onClick={doAdd} className="btn-cta text-xs" data-testid={`day-manual-save-${date}`}>Aggiungi</button>
        </div>
      )}

      {cards.length === 0 ? (
        <div className="border-2 border-dashed border-[#0A0A0A]/30 p-6 text-center text-[#0A0A0A]/50 text-sm">
          Nessuna scheda per questo giorno. Carica il PDF o aggiungi manualmente.
        </div>
      ) : (
        <div className="space-y-4">
          {cards.map((c) => (
            <CardItem
              key={c.id}
              card={c}
              existingContract={contractsIndex?.[(c.artist_name || "").trim().toLowerCase()] || null}
              onStatus={(id, s, cb, extra) => onStatus(id, s, cb, extra)}
              onDelete={onDelete}
              onNoteAdded={(n) => onNoteAdded(c.id, n)}
              onNoteRemoved={(nid) => onNoteRemoved(c.id, nid)}
              onRefresh={() => window.dispatchEvent(new Event("mg-refresh"))}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function RescheduleLogBox({ log }) {
  const [open, setOpen] = useState(false);
  if (!log || log.length === 0) return null;
  const last = log[log.length - 1];
  return (
    <div className="mb-4 border-2 border-blue-600 bg-blue-50/50" data-testid="reschedule-log-box">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-blue-100/60">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-blue-700">
          <RefreshCw size={12} /> Riprogrammazioni ({log.length})
          <span className="normal-case tracking-normal font-normal text-[#0A0A0A]/60">
            — ultima: {new Date(last.from_date).toLocaleDateString("it-IT")}{last.from_time ? ` ${last.from_time}` : ""} → {new Date(last.to_date).toLocaleDateString("it-IT")}{last.to_time ? ` ${last.to_time}` : ""}
          </span>
        </div>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="px-4 pb-3 pt-1 space-y-1.5 text-sm border-t border-blue-600/30">
          {log.map((e, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 py-1 border-b border-blue-600/10 last:border-0" data-testid={`reschedule-entry-${i}`}>
              <span className="text-[10px] uppercase tracking-widest text-blue-700 font-bold">#{i + 1}</span>
              <span className="font-mono text-xs text-[#0A0A0A]/70">
                {new Date(e.from_date).toLocaleDateString("it-IT")}{e.from_time ? ` ${e.from_time}` : ""}
              </span>
              <ChevronRight size={12} className="text-blue-700" />
              <span className="font-mono text-xs text-[#0A0A0A] font-bold">
                {new Date(e.to_date).toLocaleDateString("it-IT")}{e.to_time ? ` ${e.to_time}` : ""}
              </span>
              {e.reason && <span className="text-xs text-[#0A0A0A]/60 italic">— "{e.reason}"</span>}
              {e.by && <span className="ml-auto text-[10px] text-[#0A0A0A]/40">di {e.by}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CardItem({ card, existingContract, onStatus, onDelete, onNoteAdded, onNoteRemoved, onRefresh }) {
  const [showTimeline, setShowTimeline] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [tLoading, setTLoading] = useState(false);
  const [isManager, setIsManager] = useState(!!card.is_manager);
  const [mgrToggling, setMgrToggling] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showMerge, setShowMerge] = useState(false);
  // Badge NUOVO: card appena assegnata dal Setting e non ancora aperta dal venditore
  const isUnreadAssignment = !!card.assigned_vendor_id && !card.read_by_vendor_at;
  const [isUnread, setIsUnread] = useState(isUnreadAssignment);
  useEffect(() => { setIsUnread(isUnreadAssignment); }, [isUnreadAssignment]);

  // Marca la card come "letta" al primo render (il venditore sta vedendo la card).
  // Scatta solo per i lead assegnati dal Setting ancora non visualizzati.
  useEffect(() => {
    if (!isUnreadAssignment) return;
    const t = setTimeout(() => {
      api.post(`/mio-giorno/card/${card.id}/mark-read`).then(() => {
        setIsUnread(false);
        window.dispatchEvent(new Event("mg-unread-refresh"));
      }).catch(() => { /* silente */ });
    }, 1500); // 1,5s: evita che l'utente scrolli velocemente e non lo veda
    return () => clearTimeout(t);
  }, [card.id, isUnreadAssignment]);

  const navigate = useNavigate();

  // Sync when parent refreshes the card object
  useEffect(() => { setIsManager(!!card.is_manager); }, [card.is_manager]);

  const toggleManager = async () => {
    const next = !isManager;
    setMgrToggling(true);
    // Optimistic UI
    setIsManager(next);
    try {
      await api.post("/mio-giorno/leads/set-manager", { artist_name: card.artist_name, is_manager: next });
      toast.success(next
        ? `${card.artist_name} etichettato come Manager di artisti`
        : `Etichetta Manager rimossa a ${card.artist_name}`);
      // Notify parent list to refresh so other cards with same name update
      window.dispatchEvent(new Event("mg-refresh"));
    } catch (e) {
      setIsManager(!next); // revert
      toast.error(e?.response?.data?.detail || "Errore aggiornamento");
    } finally {
      setMgrToggling(false);
    }
  };

  const loadTimeline = async () => {
    if (showTimeline) { setShowTimeline(false); return; }
    setTLoading(true);
    try {
      const res = await api.get(`/mio-giorno/artist-timeline?name=${encodeURIComponent(card.artist_name)}`);
      setTimeline(res.data || []);
      setShowTimeline(true);
    } catch (e) {
      toast.error("Errore timeline");
    } finally {
      setTLoading(false);
    }
  };

  // Best-effort extraction of email + phone from the loosely-typed card.fields
  const extractContact = () => {
    const f = card.fields || {};
    let email = "";
    let telefono = "";
    for (const [k, v] of Object.entries(f)) {
      const val = String(v || "").trim();
      const keyLower = k.toLowerCase();
      if (!email && (keyLower.includes("email") || keyLower.includes("mail"))) email = val;
      if (!telefono && (keyLower.includes("tel") || keyLower.includes("cell") || keyLower.includes("phone") || keyLower.includes("whatsapp"))) telefono = val;
      // Regex fallbacks
      if (!email) { const m = val.match(/[\w.+-]+@[\w-]+\.[\w.-]+/); if (m) email = m[0]; }
      if (!telefono) { const m = val.match(/\+?\d[\d\s.-]{7,}/); if (m) telefono = m[0].trim(); }
    }
    return { email, telefono };
  };

  const goToContract = async () => {
    const name = card.artist_name || "";
    const { email, telefono } = extractContact();

    // Se è un manager di artisti: sempre nuovo contratto/preventivo
    if (isManager) {
      navigate("/admin/contratti", {
        state: { prefill: { nome_cognome: name, email, telefono, is_manager: true } },
      });
      return;
    }

    // If we already know a contract exists (from parent index), open it directly
    if (existingContract?.id) {
      toast.info(`Apro contratto esistente di ${name}`);
      navigate("/admin/contratti", { state: { open_contract_id: existingContract.id } });
      return;
    }
    // Otherwise double-check server-side (index could be stale) then create new
    try {
      const r = await api.get(`/contratti/find-by-name?name=${encodeURIComponent(name)}`);
      const existing = (r.data || [])[0];
      if (existing) {
        toast.info(`Apro contratto esistente di ${name}`);
        navigate("/admin/contratti", { state: { open_contract_id: existing.id } });
        return;
      }
    } catch (e) { /* fall through to create */ }
    navigate("/admin/contratti", {
      state: { prefill: { nome_cognome: name, email, telefono } },
    });
  };

  return (
    <div className="border-2 border-[#0A0A0A] bg-[#FAFAF7]" data-testid={`mio-giorno-card-${card.id}`}>
      <div className="flex items-center justify-between p-5 border-b-2 border-[#0A0A0A]/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#FF5A1F] text-white flex items-center justify-center font-display text-lg uppercase">
            {(card.artist_name || "?").slice(0, 1)}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display text-2xl uppercase text-[#0A0A0A]">{card.artist_name}</div>
              {isUnread && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 border-2 border-[#FF5A1F] bg-[#FF5A1F] text-white text-[10px] uppercase tracking-widest font-bold animate-pulse" data-testid={`unread-badge-${card.id}`}>
                  🔥 Nuovo per te
                </span>
              )}
              {card.scheduled_time && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 border-2 border-[#0A0A0A] bg-[#0A0A0A] text-white text-[10px] uppercase tracking-widest font-bold" data-testid={`card-time-${card.id}`}>
                  <CalendarClock size={10} /> <ItalyTimeInline dateStr={card.date} hhmm={card.scheduled_time} />
                </span>
              )}
              {isManager && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 border-2 border-[#FF5A1F] bg-[#FFF5EF] text-[#FF5A1F] text-[10px] uppercase tracking-widest font-bold" data-testid={`manager-badge-${card.id}`}>
                  <UserCog size={10} /> Manager artisti
                </span>
              )}
              {card.source === "calendar_sync" && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 border-2 border-blue-600 bg-blue-50 text-blue-700 text-[10px] uppercase tracking-widest font-bold" title="Scheda creata/aggiornata automaticamente dalla prenotazione Google Calendar" data-testid={`sync-badge-${card.id}`}>
                  <Calendar size={10} /> Da Calendar
                </span>
              )}
            </div>
            {card.status && (
              <div className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 border text-[10px] uppercase tracking-widest ${STATUS_META[card.status]?.cls || ""}`}>
                {React.createElement(STATUS_META[card.status]?.icon || CheckCircle2, { size: 10 })}
                {STATUS_META[card.status]?.label}
                {card.call_back_date && ` — ${card.call_back_date}`}
                {card.status === "next_call" && card.next_call_date && (
                  <> — {new Date(card.next_call_date).toLocaleDateString("it-IT")}{card.next_call_time ? ` ${card.next_call_time}` : ""}</>
                )}
              </div>
            )}
            {card.rescheduled_from?.date && (
              <div className="inline-flex items-center gap-1 mt-1 ml-1 px-2 py-0.5 border-2 border-blue-600 bg-blue-50 text-blue-700 text-[10px] uppercase tracking-widest">
                <RefreshCw size={10} /> Riprogrammata da {new Date(card.rescheduled_from.date).toLocaleDateString("it-IT")}{card.rescheduled_from.time ? ` ${card.rescheduled_from.time}` : ""}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDetails(true)}
            className="p-2 border-2 border-[#0A0A0A]/30 hover:border-[#FF5A1F] hover:text-[#FF5A1F]"
            title="Modifica dati scheda (numero WhatsApp, email, orario call…) — non crea contratti"
            data-testid={`card-details-btn-${card.id}`}
          >
            <Settings size={14} />
          </button>
          <button
            onClick={() => setShowMerge(true)}
            className="p-2 border-2 border-[#0A0A0A]/30 hover:border-[#FF5A1F] hover:text-[#FF5A1F]"
            title="Unisci con un'altra scheda dello stesso cliente (recupera campi + note vocali)"
            data-testid={`card-merge-btn-${card.id}`}
          >
            <Link2 size={14} />
          </button>
          <button
            onClick={toggleManager}
            disabled={mgrToggling}
            className={`p-2 border-2 transition-colors ${
              isManager
                ? "border-[#FF5A1F] bg-[#FF5A1F] text-white hover:bg-[#0A0A0A] hover:border-[#0A0A0A]"
                : "border-[#0A0A0A]/30 text-[#0A0A0A]/60 hover:border-[#FF5A1F] hover:text-[#FF5A1F]"
            } disabled:opacity-50`}
            title={isManager ? "Rimuovi etichetta Manager di artisti" : "Etichetta come Manager di artisti (permette più contratti)"}
            data-testid={`card-manager-toggle-${card.id}`}
          >
            {mgrToggling ? <Loader2 size={14} className="animate-spin" /> : <UserCog size={14} />}
          </button>
          <button
            onClick={goToContract}
            className={`px-3 py-2 border-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 transition-colors ${
              isManager
                ? "border-[#FF5A1F] bg-[#FF5A1F] text-white hover:bg-[#0A0A0A] hover:border-[#0A0A0A]"
                : existingContract
                  ? "border-green-600 bg-green-600 text-white hover:bg-green-700 hover:border-green-700"
                  : "border-[#FF5A1F] bg-[#FF5A1F] text-white hover:bg-[#0A0A0A] hover:border-[#0A0A0A]"
            }`}
            title={isManager
              ? "Crea un nuovo contratto/preventivo (Manager: sono ammessi più contratti)"
              : (existingContract ? "Apri contratto esistente" : "Crea nuovo contratto per questo artista")}
            data-testid={`card-contract-${card.id}`}
          >
            <FilePen size={12} />
            {isManager
              ? "Nuovo contratto"
              : (existingContract ? "Vedi contratto" : "Contratto")}
          </button>
          <button onClick={loadTimeline} className="p-2 border border-[#0A0A0A]/30 hover:border-[#FF5A1F] hover:text-[#FF5A1F]" title="Timeline artista" data-testid={`card-timeline-${card.id}`}>
            {tLoading ? <Loader2 size={14} className="animate-spin" /> : <History size={14} />}
          </button>
          <button onClick={() => onDelete(card.id)} className="p-2 border border-[#0A0A0A]/30 hover:border-red-500 hover:text-red-500" title="Elimina" data-testid={`card-delete-${card.id}`}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Provenienza Meta + storia Setting (visibile al venditore se il lead
          è stato assegnato dal Setting · Prima call) */}
      {(card.meta_platform || card.meta_ad_name || card.meta_campaign_name || card.setting_log) && (
        <div className="p-5 border-b-2 border-[#0A0A0A]/10 bg-blue-50/40 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest font-bold text-blue-900">
              📊 Da dove arriva questo lead
            </div>
            {card.assigned_vendor_name && (
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#FF5A1F]">
                Assegnato a {card.assigned_vendor_name}
              </div>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-1 text-xs">
            {card.meta_platform && (
              <div>
                <b>Piattaforma:</b>{" "}
                <span className={`font-bold ${card.meta_platform === "fb" ? "text-blue-700" : card.meta_platform === "ig" ? "text-pink-600" : "text-[#0A0A0A]"}`}>
                  {card.meta_platform === "fb" ? "📘 Facebook" : card.meta_platform === "ig" ? "📸 Instagram" : card.meta_platform.toUpperCase()}
                </span>
                {" · "}
                <span className="italic text-[#0A0A0A]/60">{card.meta_is_organic ? "lead organico" : "da inserzione"}</span>
              </div>
            )}
            {card.meta_campaign_name && <div><b>Campagna:</b> {card.meta_campaign_name}</div>}
            {card.meta_adset_name && <div><b>Adset:</b> {card.meta_adset_name}</div>}
            {card.meta_ad_name && <div><b>Inserzione:</b> {card.meta_ad_name}</div>}
            {card.meta_form_name && <div><b>Modulo (form):</b> {card.meta_form_name}</div>}
          </div>
          {(card.setting_log || []).length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-blue-900 mb-1">
                📞 Cosa ha fatto il setter prima
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {[...card.setting_log].reverse().map((e, i) => (
                  <div key={i} className="text-xs border-l-4 border-blue-700 bg-white/60 px-2 py-1">
                    <div className="flex justify-between gap-2">
                      <b className="text-blue-900">{(e.status || "").replace(/_/g, " ")}</b>
                      <span className="text-[#0A0A0A]/50">
                        {new Date(e.at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                    {e.note && <div className="text-[#0A0A0A]/70 mt-0.5">{e.note}</div>}
                    {e.by && <div className="text-[9px] text-[#0A0A0A]/40 uppercase tracking-widest">by {e.by}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Fields */}
      {Object.keys(card.fields || {}).length > 0 && (
        <div className="p-5 space-y-3">
          <div className="flex items-center justify-between border-b border-[#0A0A0A]/10 pb-2 mb-2">
            <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/40 font-bold">Dati scheda</div>
            <button
              onClick={async () => {
                if (!window.confirm(
                  `Svuoto TUTTI i campi della scheda "${card.artist_name}" tranne telefono/email.\n\n` +
                  `Usa questo se vedi dati che non sono di questo cliente.\nNome, orario, status e note vocali restano intatti.\n\nProcedo?`
                )) return;
                try {
                  const r = await api.post(`/mio-giorno/card/${card.id}/clear-fields`);
                  toast.success(`Svuotati ${r.data.removed_fields} campi — telefono/email preservati`);
                  onRefresh?.();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Errore");
                }
              }}
              className="text-[9px] uppercase tracking-widest font-bold text-red-600 hover:text-white hover:bg-red-600 border border-red-500 px-2 py-0.5 inline-flex items-center gap-1"
              title="Svuota tutti i campi tranne contatti (usa se la scheda ha dati di altri clienti copiati per errore)"
              data-testid={`card-clear-fields-${card.id}`}
            >
              <Trash2 size={10} /> Svuota campi
            </button>
          </div>
          {Object.entries(card.fields).map(([k, v]) => (
            <div key={k}>
              <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold">{k}</div>
              <div className="text-sm text-[#0A0A0A]/85 mt-1">{v}</div>
            </div>
          ))}
        </div>
      )}

      {/* Voice recorder + status */}
      <div className="p-5 border-t-2 border-[#0A0A0A]/10 bg-[#F2F2EC] space-y-4">
        <MeetSyncButton card={card} onImported={onNoteAdded} />
        <VoiceRecorder
          cardId={card.id}
          onSaved={onNoteAdded}
        />

        {(card.notes || []).length > 0 && (
          <div className="space-y-2">
            {card.notes.map((n) => (
              <div key={n.id} className="border-2 border-[#0A0A0A] bg-[#FAFAF7] p-3" data-testid={`note-${n.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/50">Nota vocale · {new Date(n.created_at).toLocaleString("it-IT")}</div>
                    <div className="mt-1 text-sm text-[#0A0A0A]/85 italic">
                      {n.text || <span className="text-[#0A0A0A]/40">Trascrizione non disponibile</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <SendNoteToManagerButton note={n} artistName={card.artist_name} />
                    <button
                      onClick={async () => {
                        if (!window.confirm("Eliminare questa nota?")) return;
                        try { await api.delete(`/mio-giorno/card/${card.id}/voice-note/${n.id}`); onNoteRemoved(n.id); } catch { toast.error("Errore"); }
                      }}
                      className="p-1 text-[#0A0A0A]/40 hover:text-red-500"
                      data-testid={`note-delete-${n.id}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {(card.reschedule_log || []).length > 0 && (
          <RescheduleLogBox log={card.reschedule_log} />
        )}

        <StatusButtons card={card} onStatus={onStatus} onRescheduled={() => window.dispatchEvent(new Event("mg-refresh"))} />
      </div>

      {showTimeline && (
        <div className="p-5 border-t-2 border-[#0A0A0A]/10 bg-white" data-testid={`timeline-${card.id}`}>
          <div className="text-xs uppercase tracking-widest text-[#FF5A1F] font-bold mb-3">Storico {card.artist_name}</div>
          {timeline.length === 0 ? (
            <div className="text-sm text-[#0A0A0A]/50">Nessun'altra scheda per questo artista.</div>
          ) : (
            <ul className="space-y-2 text-sm">
              {timeline.map((t) => (
                <li key={t.id} className="flex items-center gap-3">
                  <span className="font-mono text-xs text-[#0A0A0A]/60">{t.date}</span>
                  {t.status && (
                    <span className={`px-2 py-0.5 border text-[10px] uppercase ${STATUS_META[t.status]?.cls || ""}`}>
                      {STATUS_META[t.status]?.label}
                    </span>
                  )}
                  <span className="text-[#0A0A0A]/70">{(t.notes || []).length} note</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showDetails && (
        <CardDetailsModal
          card={card}
          onClose={() => setShowDetails(false)}
          onSaved={() => { setShowDetails(false); onRefresh?.(); }}
        />
      )}

      {showMerge && (
        <MergeCardModal
          card={card}
          onClose={() => setShowMerge(false)}
          onMerged={() => { setShowMerge(false); onRefresh?.(); }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Modale "Unisci con..."
// Permette di scegliere una scheda dello stesso cliente (anche con nome diverso)
// da cui recuperare CAMPI + NOTE VOCALI + flag manager.
// -----------------------------------------------------------------------------
function MergeCardModal({ card, onClose, onMerged }) {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(card.artist_name || "");
  const [selectedArtist, setSelectedArtist] = useState("");
  const [artistCards, setArtistCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [selectedSource, setSelectedSource] = useState("");
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/mio-giorno/artists-list");
        setArtists(r.data || []);
      } catch (e) {
        toast.error("Errore caricamento lista artisti");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return artists;
    return artists.filter((a) =>
      (a.display_name || "").toLowerCase().includes(s)
    );
  }, [q, artists]);

  const pickArtist = async (name) => {
    setSelectedArtist(name);
    setSelectedSource("");
    setArtistCards([]);
    setLoadingCards(true);
    try {
      const r = await api.get(`/mio-giorno/artist-cards?name=${encodeURIComponent(name)}`);
      // filtra fuori la card corrente
      const list = (r.data || []).filter((c) => c.id !== card.id);
      setArtistCards(list);
      if (list.length === 0) {
        toast.info("Nessun'altra scheda trovata per questo artista");
      }
    } catch (e) {
      toast.error("Errore caricamento schede");
    } finally {
      setLoadingCards(false);
    }
  };

  const doMerge = async () => {
    if (!selectedSource) {
      toast.error("Seleziona la scheda da cui recuperare i dati");
      return;
    }
    setMerging(true);
    try {
      const r = await api.post(`/mio-giorno/card/${card.id}/merge-from`, {
        source_card_id: selectedSource,
        include_fields: true,
        include_notes: true,
      });
      const { added_fields, added_notes, message } = r.data || {};
      if (added_fields === 0 && added_notes === 0) {
        toast.info(message || "Nessun dato aggiuntivo da copiare");
      } else {
        toast.success(`Recuperati ${added_fields} campi e ${added_notes} note vocali`);
      }
      onMerged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore merge");
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="merge-card-modal">
      <div className="bg-[#FAFAF7] border-2 border-[#0A0A0A] max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b-2 border-[#0A0A0A] p-4 flex items-center justify-between sticky top-0 bg-[#FAFAF7]">
          <div>
            <div className="font-display text-2xl uppercase text-[#0A0A0A]">Unisci scheda</div>
            <div className="text-xs text-[#0A0A0A]/60 mt-1">
              Recupera campi + note vocali da un'altra scheda dello stesso cliente
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:text-[#FF5A1F]" data-testid="merge-close-btn">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="border border-[#FF5A1F] bg-[#FFF5EF] p-3 text-xs">
            <b>Scheda corrente:</b> {card.artist_name} — {card.date}
            {card.scheduled_time ? ` ${card.scheduled_time}` : ""}
            <div className="text-[#0A0A0A]/60 mt-0.5">
              {Object.keys(card.fields || {}).length} campi · {(card.notes || []).length} note vocali
            </div>
          </div>

          {!selectedArtist && (
            <>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/60 mb-1 font-bold">
                  Cerca l'artista da cui recuperare i dati
                </div>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0A0A0A]/40" />
                  <input
                    autoFocus
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Nome artista..."
                    className="w-full border-2 border-[#0A0A0A] pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#FF5A1F]"
                    data-testid="merge-search-input"
                  />
                </div>
              </div>

              {loading ? (
                <div className="text-center py-8"><Loader2 className="animate-spin inline" size={20} /></div>
              ) : (
                <div className="max-h-80 overflow-y-auto border border-[#0A0A0A]/20">
                  {filtered.length === 0 && (
                    <div className="p-4 text-center text-sm text-[#0A0A0A]/50">Nessun artista trovato</div>
                  )}
                  {filtered.map((a) => (
                    <button
                      key={a.display_name}
                      onClick={() => pickArtist(a.display_name)}
                      className="w-full text-left px-3 py-2 border-b border-[#0A0A0A]/10 hover:bg-[#FFF5EF] transition-colors flex items-center justify-between gap-2"
                      data-testid={`merge-artist-${a.display_name}`}
                    >
                      <div>
                        <div className="font-bold text-sm">{a.display_name}</div>
                        <div className="text-[10px] text-[#0A0A0A]/50">
                          {a.cards_count} schede · max {a.max_fields} campi · {a.notes_total} note
                        </div>
                      </div>
                      <ChevronRight size={14} className="text-[#0A0A0A]/40" />
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {selectedArtist && (
            <>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setSelectedArtist(""); setArtistCards([]); setSelectedSource(""); }}
                  className="text-xs text-[#0A0A0A]/60 hover:text-[#FF5A1F] inline-flex items-center gap-1"
                  data-testid="merge-back-btn"
                >
                  <ChevronLeft size={14} /> Cambia artista
                </button>
                <span className="text-sm font-bold ml-2">{selectedArtist}</span>
              </div>

              <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/60 font-bold">
                Scegli la scheda sorgente
              </div>

              {loadingCards ? (
                <div className="text-center py-8"><Loader2 className="animate-spin inline" size={20} /></div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {artistCards.map((c) => {
                    const nf = Object.keys(c.fields || {}).length;
                    const nn = (c.notes || []).length;
                    return (
                      <label
                        key={c.id}
                        className={`block border-2 p-3 cursor-pointer transition-colors ${
                          selectedSource === c.id
                            ? "border-[#FF5A1F] bg-[#FFF5EF]"
                            : "border-[#0A0A0A]/20 hover:border-[#FF5A1F]/50"
                        }`}
                        data-testid={`merge-source-card-${c.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="radio"
                            name="mergeSource"
                            checked={selectedSource === c.id}
                            onChange={() => setSelectedSource(c.id)}
                            className="mt-1"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-sm">{c.artist_name}</span>
                              <span className="text-xs text-[#0A0A0A]/60">{c.date}{c.scheduled_time ? ` · ${c.scheduled_time}` : ""}</span>
                            </div>
                            <div className="text-[10px] text-[#0A0A0A]/60 mt-0.5">
                              {nf} campi · {nn} note vocali
                            </div>
                            {nf > 0 && (
                              <div className="mt-2 text-xs space-y-0.5">
                                {Object.entries(c.fields || {}).slice(0, 4).map(([k, v]) => (
                                  <div key={k} className="truncate">
                                    <span className="text-[#FF5A1F] font-bold">{k}:</span>{" "}
                                    <span className="text-[#0A0A0A]/70">{String(v).slice(0, 80)}</span>
                                  </div>
                                ))}
                                {nf > 4 && (
                                  <div className="text-[#0A0A0A]/40">+{nf - 4} altri campi...</div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        <div className="border-t-2 border-[#0A0A0A] p-4 flex items-center justify-end gap-2 sticky bottom-0 bg-[#FAFAF7]">
          <button
            onClick={onClose}
            className="px-4 py-2 border-2 border-[#0A0A0A]/30 text-sm uppercase tracking-widest font-bold hover:border-[#0A0A0A]"
            data-testid="merge-cancel-btn"
          >
            Annulla
          </button>
          <button
            onClick={doMerge}
            disabled={!selectedSource || merging}
            className="px-4 py-2 border-2 border-[#FF5A1F] bg-[#FF5A1F] text-white text-sm uppercase tracking-widest font-bold hover:bg-[#0A0A0A] hover:border-[#0A0A0A] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            data-testid="merge-confirm-btn"
          >
            {merging ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
            Unisci nella scheda corrente
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Modale "Dettagli scheda lead"
// Permette di salvare telefono/WhatsApp, email, orario call, note contatto SENZA
// creare/toccare i contratti. Serve per registrare i dati di contatto quando
// non hai ancora deciso servizi o importo.
// -----------------------------------------------------------------------------
function CardDetailsModal({ card, onClose, onSaved }) {
  const initial = card.fields || {};
  const [name, setName] = useState(card.artist_name || "");
  const [time, setTime] = useState(card.scheduled_time || "");
  const [telefono, setTelefono] = useState(initial.telefono || initial.whatsapp || initial.cellulare || "");
  const [email, setEmail] = useState(initial.email || "");
  const [note, setNote] = useState(initial.note_contatto || "");
  const [indirizzo, setIndirizzo] = useState(initial.indirizzo || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      // Preserva le chiavi custom già presenti nella card
      const fields = {
        ...initial,
        telefono: telefono.trim(),
        email: email.trim(),
        note_contatto: note.trim(),
        indirizzo: indirizzo.trim(),
      };
      // Rimuovi chiavi duplicate/vecchie che ora sono normalizzate in telefono
      if (telefono.trim()) {
        delete fields.whatsapp;
        delete fields.cellulare;
      }
      await api.patch(`/mio-giorno/card/${card.id}/details`, {
        artist_name: name.trim() || card.artist_name,
        scheduled_time: time,
        fields,
      });
      toast.success("Dettagli scheda salvati");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="card-details-modal">
      <div className="bg-white border-4 border-[#0A0A0A] max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="border-b-2 border-[#0A0A0A] p-4 bg-[#F2F2EC] flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#FF5A1F]">Dettagli scheda lead</div>
            <div className="font-display text-2xl uppercase text-[#0A0A0A] mt-1">{card.artist_name}</div>
            <div className="text-[10px] text-[#0A0A0A]/60 mt-1">Salva contatti e orario call senza creare un contratto. Verrà usato come prefill quando genererai il contratto vero.</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#0A0A0A] hover:text-white border-2 border-[#0A0A0A]" data-testid="card-details-close">
            <X size={14} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Nome</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm" data-testid="card-details-name" />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Orario call (HH:MM)</label>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm font-mono" data-testid="card-details-time" />
            <div className="text-[10px] text-[#0A0A0A]/50 mt-1">Serve per ordinare le schede come il calendario</div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">📱 Numero WhatsApp / cellulare</label>
            <input
              type="tel"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              placeholder="+39 333 1234567"
              className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm font-mono"
              data-testid="card-details-telefono"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">✉️ Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="artista@email.it"
              className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm"
              data-testid="card-details-email"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Indirizzo (opzionale)</label>
            <input
              value={indirizzo}
              onChange={(e) => setIndirizzo(e.target.value)}
              placeholder="Via, città, CAP"
              className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm"
              data-testid="card-details-indirizzo"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Note contatto (opzionale)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Es. preferisce essere ricontattato di sera"
              className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm"
              data-testid="card-details-note"
            />
          </div>
        </div>

        <div className="border-t-2 border-[#0A0A0A] p-4 bg-[#F2F2EC] flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border-2 border-[#0A0A0A] text-xs uppercase tracking-widest" data-testid="card-details-cancel">
            Annulla
          </button>
          <button onClick={save} disabled={saving} className="px-4 py-2 border-2 border-[#FF5A1F] bg-[#FF5A1F] text-white text-xs uppercase tracking-widest font-bold hover:bg-[#0A0A0A] hover:border-[#0A0A0A] disabled:opacity-60 flex items-center gap-2" data-testid="card-details-save">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Salva
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusButtons({ card, onStatus, onRescheduled }) {
  const [askDate, setAskDate] = useState(false);
  const [callDate, setCallDate] = useState(card.call_back_date || todayISO());
  const [askReschedule, setAskReschedule] = useState(false);
  const [rescDate, setRescDate] = useState(card.next_call_date || todayISO());
  const [rescTime, setRescTime] = useState(card.next_call_time || "");
  const [rescReason, setRescReason] = useState("");
  const [rescLoading, setRescLoading] = useState(false);
  // Dialog motivo per rimanda / scomparso
  const [reasonDialog, setReasonDialog] = useState(null); // { status: 'rimanda'|'scomparso' }
  const [reasonValue, setReasonValue] = useState("");
  const [reasonNote, setReasonNote] = useState("");

  const openReasonDialog = (status) => {
    const defaults = status === "rimanda" ? "finanziario" : "prima_call";
    setReasonValue(card.status === status ? (card.status_reason || defaults) : defaults);
    setReasonNote(card.status_note || "");
    setReasonDialog({ status });
  };
  const confirmReason = () => {
    if (!reasonDialog) return;
    onStatus(card.id, reasonDialog.status, null, {
      status_reason: reasonValue,
      status_note: reasonNote.trim(),
    });
    setReasonDialog(null);
    setReasonNote("");
  };

  const submitReschedule = async () => {
    if (!rescDate) { toast.error("Data mancante"); return; }
    setRescLoading(true);
    try {
      const r = await api.post(`/mio-giorno/card/${card.id}/reschedule`, {
        date: rescDate, time: rescTime, reason: rescReason,
      });
      const newCard = r.data.new_card;
      toast.success(
        `Call riprogrammata al ${new Date(rescDate).toLocaleDateString("it-IT")}${rescTime ? ` ${rescTime}` : ""}`,
        {
          action: {
            label: "Vai al giorno",
            onClick: () => window.dispatchEvent(new CustomEvent("mg-jump-to-card", { detail: { date: newCard.date, cardId: newCard.id } })),
          },
        }
      );
      setAskReschedule(false);
      setRescReason("");
      onRescheduled?.(newCard);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore riprogrammazione");
    } finally {
      setRescLoading(false);
    }
  };

  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/70 mb-2">Status</div>
      <div className="flex flex-wrap gap-2">
        {Object.entries(STATUS_META).map(([key, meta]) => {
          const active = card.status === key;
          const Icon = meta.icon;
          return (
            <button
              key={key}
              onClick={() => {
                if (key === "call_back") { setAskDate(true); return; }
                if (key === "next_call") { setAskReschedule(true); return; }
                if (key === "rimanda" || key === "scomparso") {
                  // Apre sempre il dialog (sia per impostare, sia per modificare motivo).
                  // La rimozione dello status avviene con il bottone "Togli" dentro il dialog.
                  openReasonDialog(key);
                  return;
                }
                onStatus(card.id, active ? "" : key, null, {});
              }}
              className={`px-3 py-2 text-xs uppercase tracking-widest border-2 flex items-center gap-2 transition-colors ${active ? meta.cls : "border-[#0A0A0A]/40 text-[#0A0A0A]/70 hover:border-[#0A0A0A]"}`}
              data-testid={`status-${key}-${card.id}`}
            >
              <Icon size={12} /> {meta.label}
            </button>
          );
        })}
      </div>
      {card.status === "no_show" && (
        <div className="mt-3 border-2 border-amber-600 bg-amber-50 p-3" data-testid={`no-show-recovery-${card.id}`}>
          <div className="text-[10px] uppercase tracking-widest font-bold text-amber-800 mb-2">
            {card.no_show_recovered_to ? (
              <>✅ Recuperato come <span className="underline">{STATUS_META[card.no_show_recovered_to]?.label || card.no_show_recovered_to}</span></>
            ) : "Hai recuperato questo no show? Marca l'esito finale"}
          </div>
          <select
            value={card.no_show_recovered_to || ""}
            onChange={(e) => {
              onStatus(card.id, "no_show", null, {
                status_reason: card.status_reason || null,
                status_note: card.status_note || null,
                no_show_recovered_to: e.target.value || null,
              });
            }}
            className="border-2 border-amber-700 bg-white px-2 py-1 text-xs"
            data-testid={`no-show-recovery-select-${card.id}`}
          >
            <option value="">— non ancora recuperato —</option>
            <option value="signed">Contratto firmato</option>
            <option value="attesa_firma">Attesa firma</option>
            <option value="call_back">2ª call</option>
            <option value="next_call">Altra call</option>
            <option value="rimanda">Rimanda</option>
            <option value="rinuncia">Rinuncia</option>
            <option value="scomparso">Scomparso</option>
          </select>
        </div>
      )}
      {card.status === "scomparso" && (
        <div className="mt-3 border-2 border-rose-700 bg-rose-50 p-3" data-testid={`scomparso-recovery-${card.id}`}>
          <div className="text-[10px] uppercase tracking-widest font-bold text-rose-800 mb-2">
            {card.scomparso_recovered_to ? (
              <>✅ Recuperato come <span className="underline">{STATUS_META[card.scomparso_recovered_to]?.label || card.scomparso_recovered_to}</span></>
            ) : "Hai recuperato questo scomparso? Marca l'esito finale"}
          </div>
          <select
            value={card.scomparso_recovered_to || ""}
            onChange={(e) => {
              onStatus(card.id, "scomparso", null, {
                status_reason: card.status_reason || null,
                status_note: card.status_note || null,
                scomparso_recovered_to: e.target.value || null,
              });
            }}
            className="border-2 border-rose-700 bg-white px-2 py-1 text-xs"
            data-testid={`scomparso-recovery-select-${card.id}`}
          >
            <option value="">— non ancora recuperato —</option>
            <option value="signed">Contratto firmato</option>
            <option value="attesa_firma">Attesa firma</option>
            <option value="call_back">2ª call</option>
            <option value="next_call">Altra call</option>
            <option value="rimanda">Rimanda</option>
            <option value="rinuncia">Rinuncia</option>
          </select>
        </div>
      )}
      {askDate && (
        <div className="mt-3 flex items-center gap-2" data-testid={`askdate-${card.id}`}>
          <input
            type="date"
            value={callDate}
            onChange={(e) => setCallDate(e.target.value)}
            className="border-2 border-[#0A0A0A] px-2 py-1 text-sm font-mono"
          />
          <button
            onClick={() => { onStatus(card.id, "call_back", callDate, {}); setAskDate(false); }}
            className="btn-cta text-xs"
            data-testid={`confirm-callback-${card.id}`}
          >
            Conferma richiamo
          </button>
          <button onClick={() => setAskDate(false)} className="text-xs text-[#0A0A0A]/50 hover:text-[#0A0A0A]">Annulla</button>
        </div>
      )}
      {askReschedule && (
        <div className="mt-3 border-2 border-blue-600 bg-blue-50 p-3" data-testid={`reschedule-${card.id}`}>
          <div className="text-[10px] uppercase tracking-widest font-bold text-blue-700 mb-2 flex items-center gap-1.5">
            <CalendarClock size={12} /> Riprogramma call
          </div>
          <div className="grid md:grid-cols-[1fr_1fr_2fr] gap-2 mb-2">
            <div>
              <label className="text-[9px] uppercase tracking-widest text-[#0A0A0A]/50">Nuova data *</label>
              <input type="date" value={rescDate} onChange={(e)=>setRescDate(e.target.value)} className="mt-1 w-full border-2 border-[#0A0A0A] px-2 py-1 text-sm font-mono" data-testid={`resc-date-${card.id}`}/>
            </div>
            <div>
              <label className="text-[9px] uppercase tracking-widest text-[#0A0A0A]/50">Ora</label>
              <input type="time" value={rescTime} onChange={(e)=>setRescTime(e.target.value)} className="mt-1 w-full border-2 border-[#0A0A0A] px-2 py-1 text-sm font-mono" data-testid={`resc-time-${card.id}`}/>
            </div>
            <div>
              <label className="text-[9px] uppercase tracking-widest text-[#0A0A0A]/50">Motivo (facoltativo)</label>
              <input value={rescReason} onChange={(e)=>setRescReason(e.target.value)} placeholder="es. Artista impegnato, chiede spostamento" className="mt-1 w-full border-2 border-[#0A0A0A] px-2 py-1 text-sm" data-testid={`resc-reason-${card.id}`}/>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={submitReschedule}
              disabled={rescLoading}
              className="btn-cta text-xs disabled:opacity-60"
              data-testid={`confirm-reschedule-${card.id}`}
            >
              {rescLoading ? <Loader2 size={12} className="animate-spin"/> : <CalendarClock size={12}/>}
              Riprogramma e crea scheda
            </button>
            <button onClick={()=>setAskReschedule(false)} className="text-xs text-[#0A0A0A]/50 hover:text-[#0A0A0A]">Annulla</button>
          </div>
        </div>
      )}
      {reasonDialog && (
        <div
          className={`mt-3 border-2 p-3 ${reasonDialog.status === "rimanda" ? "border-indigo-600 bg-indigo-50" : "border-rose-700 bg-rose-50"}`}
          data-testid={`reason-dialog-${card.id}`}
        >
          <div className={`text-[10px] uppercase tracking-widest font-bold mb-2 ${reasonDialog.status === "rimanda" ? "text-indigo-700" : "text-rose-800"}`}>
            {reasonDialog.status === "rimanda" ? "Motivo del rinvio" : "Quando è scomparso?"}
          </div>
          <div className="grid md:grid-cols-[1fr_2fr] gap-2 mb-2">
            <select
              value={reasonValue}
              onChange={(e) => setReasonValue(e.target.value)}
              className="border-2 border-[#0A0A0A] bg-white px-2 py-1 text-sm"
              data-testid={`reason-select-${card.id}`}
            >
              {(reasonDialog.status === "rimanda" ? RIMANDA_REASONS : SCOMPARSO_REASONS).map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <input
              value={reasonNote}
              onChange={(e) => setReasonNote(e.target.value)}
              placeholder={reasonDialog.status === "rimanda" ? "Dettaglio (facoltativo): es. operazione a febbraio" : "Nota (facoltativo)"}
              className="border-2 border-[#0A0A0A] bg-white px-2 py-1 text-sm"
              data-testid={`reason-note-${card.id}`}
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={confirmReason}
              className="btn-cta text-xs"
              data-testid={`confirm-reason-${card.id}`}
            >
              Conferma
            </button>
            {card.status === reasonDialog.status && (
              <button
                onClick={() => {
                  onStatus(card.id, "", null, {});
                  setReasonDialog(null);
                  setReasonNote("");
                }}
                className="text-xs px-2 py-1 border border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition"
                data-testid={`remove-status-${card.id}`}
              >
                Togli questo status
              </button>
            )}
            <button
              onClick={() => setReasonDialog(null)}
              className="text-xs text-[#0A0A0A]/50 hover:text-[#0A0A0A]"
            >
              Annulla
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MeetSyncButton({ card, onImported }) {
  const [busy, setBusy] = useState(false);
  const [art, setArt] = useState(card.meet_artifacts || null);
  if (!card.calendar_event_id) return null;
  const sync = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/mio-giorno/cards/${card.id}/meet-sync`);
      setArt(r.data);
      const map = {
        ready: "Registrazione + trascrizione importate",
        processing: "Meet sta ancora elaborando — riprova tra qualche minuto",
        pending: "Nessun record Meet trovato (riunione non registrata?)",
        ambiguous: "Trovato più di un record — controllo manuale",
        missing: "Riunione terminata ma nessun file disponibile",
      };
      toast.success(map[r.data?.status] || "Sincronizzazione eseguita");
      if (r.data?.transcript_text && onImported) onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore sync Google Meet");
    } finally { setBusy(false); }
  };
  return (
    <div className="border-2 border-blue-700 bg-blue-50/40 p-3 space-y-2" data-testid={`meet-sync-${card.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-widest font-bold text-blue-900">
          🎥 Google Meet · registrazione & trascrizione
        </div>
        <button
          onClick={sync}
          disabled={busy}
          className="px-3 py-1.5 border-2 border-blue-700 bg-white text-blue-700 text-[10px] uppercase tracking-widest font-bold hover:bg-blue-700 hover:text-white disabled:opacity-50"
          data-testid={`meet-sync-btn-${card.id}`}
        >
          {busy ? "Sync…" : (art?.status === "ready" ? "↻ Risincronizza" : "Importa da Meet")}
        </button>
      </div>
      {art?.status && (
        <div className="text-xs">
          Stato: <b className={art.status === "ready" ? "text-emerald-700" : "text-[#0A0A0A]/70"}>{art.status}</b>
          {art.reason && <span className="text-[#0A0A0A]/50"> — {art.reason}</span>}
        </div>
      )}
      {art?.recording_url && (
        <a href={art.recording_url} target="_blank" rel="noreferrer"
           className="inline-block text-xs text-blue-700 underline hover:text-blue-900">
          🎬 Apri registrazione (Drive)
        </a>
      )}
      {!art && (
        <div className="text-[11px] text-[#0A0A0A]/60">
          Clicca "Importa da Meet" dopo la riunione per aggiungere trascrizione e link registrazione alle note. Serve Google Workspace Business Standard+ con registrazione/trascrizione attiva.
        </div>
      )}
    </div>
  );
}


function VoiceRecorder({ cardId, onSaved }) {  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await upload(blob);
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Permesso microfono negato o non disponibile");
    }
  };

  const stop = () => {
    if (mediaRef.current && recording) {
      mediaRef.current.stop();
      setRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const upload = async (blob) => {
    setProcessing(true);
    const form = new FormData();
    form.append("audio", new File([blob], `note-${Date.now()}.webm`, { type: "audio/webm" }));
    try {
      const res = await api.post(`/mio-giorno/card/${cardId}/voice-note`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Nota vocale salvata e trascritta");
      onSaved(res.data);
    } catch (e) {
      toast.error("Errore salvataggio nota");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="flex items-center gap-3">
      {!recording ? (
        <button
          onClick={start}
          disabled={processing}
          className="btn-cta disabled:opacity-60"
          data-testid={`record-start-${cardId}`}
        >
          {processing ? <Loader2 size={16} className="animate-spin" /> : <Mic size={16} />}
          {processing ? "Trascrizione in corso..." : "Registra nota vocale"}
        </button>
      ) : (
        <button
          onClick={stop}
          className="btn-cta bg-red-600 border-red-700 animate-pulse"
          data-testid={`record-stop-${cardId}`}
        >
          <Square size={16} /> Stop ({elapsed}s)
        </button>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Pulsante "Manda nota al manager via WhatsApp"
// Apre wa.me con il numero manager (dal profilo utente) e messaggio precompilato
// con nome artista, data e testo trascritto della nota.
// -----------------------------------------------------------------------------
function SendNoteToManagerButton({ note, artistName }) {
  const { user } = useAuth();
  const managerPhone = user?.manager_phone || "";
  const clean = (managerPhone || "").replace(/[^\d+]/g, "").replace(/^\+/, "");
  const hasText = !!(note?.text || "").trim();
  const disabled = !clean || !hasText;

  const send = () => {
    if (!clean) {
      toast.error("Configura prima il numero del manager in Setup Account → WhatsApp");
      return;
    }
    if (!hasText) {
      toast.error("Trascrizione non disponibile per questa nota");
      return;
    }
    const dt = new Date(note.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    const msg = `📝 Nota da Perla Music CRM\n\nArtista: ${artistName}\nData: ${dt}\n\n${note.text}`;
    const url = `https://wa.me/${clean}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener");
  };

  return (
    <button
      type="button"
      onClick={send}
      disabled={disabled}
      title={
        !clean
          ? "Configura il numero del manager in Setup Account per abilitare l'inoltro"
          : !hasText
            ? "Nessuna trascrizione disponibile"
            : "Inoltra questa nota al manager via WhatsApp"
      }
      className={`p-1 border-2 transition-colors ${
        disabled
          ? "border-[#0A0A0A]/20 text-[#0A0A0A]/30 cursor-not-allowed"
          : "border-green-600 text-green-700 hover:bg-green-600 hover:text-white"
      }`}
      data-testid={`note-send-manager-${note.id}`}
    >
      <MessageCircle size={12} />
    </button>
  );
}

function CalendarSection({ startDate, days }) {
  const { user } = useAuth();
  const senderName = (user?.name || "").split(" ")[0] || "Perla Music";
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [scopeError, setScopeError] = useState(false);
  const [rubrica, setRubrica] = useState([]);
  const [templates, setTemplates] = useState(loadTemplates());
  const [showTplEditor, setShowTplEditor] = useState(false);
  const [sentMap, setSentMap] = useState({}); // key: `${event_id}::${type}` -> sent_at ISO
  const [createdCards, setCreatedCards] = useState({}); // event_id -> {inherited:bool, fieldsCount:number}
  const [creatingCard, setCreatingCard] = useState({}); // event_id -> true
  const remindedRef = useRef({}); // event_id -> true (already reminded in this session)

  // Crea una scheda nel Mio Giorno a partire da un evento Calendar.
  // Se l'artista ha schede storiche nel CRM, il backend eredita automaticamente
  // fields+note+is_manager+parent_card_id. Altrimenti crea una scheda vuota pronta.
  const createCardFromEvent = async (ev, artistName, evDateISO, timeStr, phone) => {
    if (!artistName || !evDateISO) {
      toast.error("Dati evento incompleti (nome artista o data mancanti)");
      return;
    }
    setCreatingCard((m) => ({ ...m, [ev.id]: true }));
    try {
      const seedFields = {};
      if (timeStr) seedFields["orario"] = timeStr;
      if (phone) seedFields["telefono"] = phone;
      const res = await api.post("/mio-giorno/card", {
        date: evDateISO,
        artist_name: artistName,
        fields: seedFields,
      });
      const inherited = !!res.data?.inherited_from_sibling;
      const fieldsCount = Object.keys(res.data?.fields || {}).length;
      const notesCount = (res.data?.notes || []).length;
      setCreatedCards((m) => ({ ...m, [ev.id]: { inherited, fieldsCount, notesCount } }));
      if (inherited) {
        toast.success(
          `✅ Scheda creata per ${artistName} con ${fieldsCount} campi e ${notesCount} note ereditati dalle schede precedenti`,
          { duration: 5000 }
        );
      } else {
        toast.success(
          `📝 Scheda vuota creata per ${artistName}. Compilala durante la call!`,
          { duration: 4500 }
        );
      }
    } catch (e) {
      toast.error(`Errore creazione scheda: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setCreatingCard((m) => ({ ...m, [ev.id]: false }));
    }
  };

  const rubricaByEmail = useMemo(() => {
    const m = {};
    for (const c of rubrica) {
      if (c.email && c.phone) m[c.email.toLowerCase()] = c.phone;
    }
    return m;
  }, [rubrica]);

  const loadRubrica = async () => {
    try {
      const res = await api.get("/rubrica");
      setRubrica(res.data || []);
    } catch (e) {
      // Silent: rubrica optional for WA
    }
  };

  const loadSent = async () => {
    try {
      const res = await api.get("/mio-giorno/wa-sent");
      const map = {};
      for (const s of res.data || []) {
        map[`${s.event_id}::${s.message_type}`] = s.sent_at;
      }
      setSentMap(map);
    } catch (e) {}
  };

  const markSent = async (evId, type) => {
    const key = `${evId}::${type}`;
    setSentMap((m) => ({ ...m, [key]: new Date().toISOString() }));
    try { await api.post("/mio-giorno/wa-sent", { event_id: evId, message_type: type }); }
    catch (e) { /* revert on error */ setSentMap((m) => { const n = { ...m }; delete n[key]; return n; }); toast.error("Errore"); }
  };
  const unmarkSent = async (evId, type) => {
    const key = `${evId}::${type}`;
    const prev = sentMap[key];
    setSentMap((m) => { const n = { ...m }; delete n[key]; return n; });
    try { await api.delete(`/mio-giorno/wa-sent?event_id=${encodeURIComponent(evId)}&message_type=${type}`); }
    catch (e) { setSentMap((m) => ({ ...m, [key]: prev })); toast.error("Errore"); }
  };

  const loadStatus = async () => {
    try {
      const res = await api.get("/mio-giorno/calendar/status");
      setStatus(res.data);
    } catch (e) {
      setStatus({ configured: false, connected: false });
    }
  };

  const loadEvents = async () => {
    setLoading(true);
    setScopeError(false);
    try {
      const res = await api.get(`/mio-giorno/calendar/events?date=${startDate}&days=${days}`);
      setEvents(res.data.events || []);
      // Il backend fa auto-sync dei guest (nome/email/telefono) nelle card del giorno.
      // Notifichiamo la sezione "Il mio giorno" perché ricarichi le card e mostri i dati aggiornati.
      window.dispatchEvent(new Event("mg-refresh"));
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (e?.response?.status === 403 && detail.includes("MISSING_CALENDAR_SCOPE")) {
        setScopeError(true);
      } else if (e?.response?.status !== 400) {
        toast.error("Errore caricamento eventi");
      }
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); loadRubrica(); loadSent(); }, []);
  useEffect(() => {
    if (status?.connected) loadEvents();
    // eslint-disable-next-line
  }, [status?.connected, startDate, days]);

  // ---- AUTO-REMINDER 15 min PRIMA della call ---------------------------------
  // Semi-automatico: quando si avvicina l'orario, apre un toast azionabile +
  // notifica del sistema operativo (se autorizzata). Con un solo click si apre
  // wa.me con il messaggio precall pre-compilato. Per l'invio 100% automatico
  // servirà Meta WhatsApp Cloud API.
  useEffect(() => {
    // Chiedi permesso notifiche una volta
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch (e) {}
    }
    // Load session state della memoria reminder (per non triggerare 2 volte)
    try {
      const raw = sessionStorage.getItem("perla.reminded");
      if (raw) remindedRef.current = JSON.parse(raw) || {};
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (!events || events.length === 0) return;
    const tick = () => {
      const now = Date.now();
      const in15 = 15 * 60 * 1000;
      const in1 = 1 * 60 * 1000;
      for (const ev of events) {
        if (ev.all_day || !ev.start) continue;
        const startTs = new Date(ev.start).getTime();
        const diff = startTs - now;
        // Firing window: -15 minuti fino a +1 minuto oltre start (per catch se il tab era chiuso)
        if (diff <= in15 && diff > -in1 && !remindedRef.current[ev.id]) {
          const artistName = cleanArtistName(ev.summary);
          const phone = resolveEventPhone(ev, rubrica, rubricaByEmail, user?.email, artistName);
          const evStart = new Date(ev.start);
          const timeItaly = evStart.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });
          const precallText = fillTemplate(templates.precall, {
            name: artistName,
            link: ev.hangout_link || ev.html_link || "",
            time: timeItaly,
            sender: senderName,
          });
          const waLink = buildWaLink(phone, precallText);
          const minsLeft = Math.max(0, Math.round(diff / 60000));
          const msg = minsLeft > 0
            ? `Call con ${artistName} tra ${minsLeft} min — invia il promemoria WhatsApp?`
            : `Call con ${artistName} sta iniziando — invia il promemoria!`;

          // 1) Notifica browser (se autorizzata)
          if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            try {
              const n = new Notification(`⏰ Promemoria call — ${artistName}`, {
                body: `${minsLeft} min alla call delle ${timeItaly}. Click per aprire WhatsApp.`,
                tag: `perla-reminder-${ev.id}`,
              });
              n.onclick = () => { window.focus(); if (waLink) window.open(waLink, "_blank", "noopener"); };
            } catch (e) {}
          }
          // 2) Toast prominente con azione
          toast(msg, {
            duration: 60000,
            action: waLink ? {
              label: "Apri WhatsApp",
              onClick: async () => {
                window.open(waLink, "_blank", "noopener");
                try {
                  await api.post("/mio-giorno/calendar/wa-sent", { event_id: ev.id, type: "precall" });
                  setSentMap((prev) => ({ ...prev, [`${ev.id}::precall`]: new Date().toISOString() }));
                } catch (e) {}
              },
            } : undefined,
          });
          // Segna come già ricordato
          remindedRef.current[ev.id] = new Date().toISOString();
          try { sessionStorage.setItem("perla.reminded", JSON.stringify(remindedRef.current)); } catch (e) {}
        }
      }
    };
    tick();
    const int = setInterval(tick, 45000); // check ogni 45 secondi
    return () => clearInterval(int);
    // eslint-disable-next-line
  }, [events, rubricaByEmail, templates, senderName]);
  // ---- fine auto-reminder ---------------------------------------------------

  // Detect ?google=connected or ?google=missing_calendar_scope after OAuth redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const g = params.get("google");
    if (g === "connected") {
      toast.success("Google Calendar collegato ✓");
      window.history.replaceState({}, "", "/admin/mio-giorno");
      loadStatus();
    } else if (g === "missing_calendar_scope") {
      toast.error(
        "Non hai autorizzato l'accesso al Calendario. Ricollega e SPUNTA la casella 'Calendar'.",
        { duration: 8000 }
      );
      window.history.replaceState({}, "", "/admin/mio-giorno");
      loadStatus();
    }
  }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const res = await api.get("/crm/google/authorize");
      window.location.href = res.data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore avvio OAuth");
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Scollegare Google Calendar?")) return;
    try {
      await api.post("/crm/google/disconnect");
      toast.success("Scollegato");
      setStatus({ ...(status || {}), connected: false, email: null });
      setEvents([]);
    } catch (e) {
      toast.error("Errore");
    }
  };

  if (!status) {
    return (
      <div className="border-2 border-[#0A0A0A] p-6 bg-[#F2F2EC] mb-6 flex items-center gap-3 text-sm text-[#0A0A0A]/60">
        <Loader2 size={16} className="animate-spin" /> Verifica connessione calendario...
      </div>
    );
  }

  if (!status.configured) {
    return (
      <div className="border-2 border-dashed border-[#0A0A0A]/30 p-6 mb-6 bg-[#F2F2EC] text-sm text-[#0A0A0A]/60" data-testid="calendar-not-configured">
        <div className="flex items-center gap-2 mb-1"><Calendar size={16} /> Calendario Google</div>
        Non ancora configurato lato server. Contatta lo sviluppatore.
      </div>
    );
  }

  if (!status.connected) {
    return (
      <div className="border-2 border-[#0A0A0A] p-6 bg-[#F2F2EC] mb-6" data-testid="calendar-disconnected">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 border-2 border-[#0A0A0A] bg-[#FAFAF7] flex items-center justify-center">
              <Calendar size={22} />
            </div>
            <div>
              <div className="font-display text-2xl uppercase text-[#0A0A0A]">Google Calendar</div>
              <div className="text-xs text-[#0A0A0A]/60">Collega il tuo account personale per vedere gli appuntamenti del giorno</div>
            </div>
          </div>
          <button onClick={connect} disabled={connecting} className="btn-cta disabled:opacity-60" data-testid="calendar-connect-btn">
            {connecting ? <Loader2 size={16} className="animate-spin" /> : <Link2 size={16} />}
            {connecting ? "Reindirizzo..." : "Collega Google Calendar"}
          </button>
        </div>
        <div className="mt-4 border-l-4 border-[#FF5A1F] bg-[#FFF5EF] p-3 text-xs text-[#0A0A0A]/80 leading-relaxed" data-testid="calendar-consent-hint">
          <b className="uppercase tracking-widest text-[#FF5A1F]">Importante</b><br/>
          Nella schermata di consenso di Google <b>spunta tutte le caselle</b>, in particolare
          "Visualizza gli eventi dei tuoi calendari Google". Se le lasci vuote, l'agenda non caricherà gli eventi.
        </div>
      </div>
    );
  }

  return (
    <div className="border-2 border-[#0A0A0A] bg-[#F2F2EC] mb-6" data-testid="calendar-section">
      <div className="p-5 border-b-2 border-[#0A0A0A]/10 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 border-2 border-[#0A0A0A] bg-[#FAFAF7] flex items-center justify-center"><Calendar size={18} /></div>
          <div>
            <div className="font-display text-2xl uppercase text-[#0A0A0A]">Agenda</div>
            <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/60">Collegato: {status.email}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowTplEditor((v) => !v)}
            className="p-2 border border-[#0A0A0A]/30 hover:border-[#FF5A1F] hover:text-[#FF5A1F]"
            title="Personalizza template WhatsApp"
            data-testid="wa-templates-btn"
          >
            <Settings size={14} />
          </button>
          <button onClick={loadEvents} className="p-2 border border-[#0A0A0A]/30 hover:border-[#FF5A1F] hover:text-[#FF5A1F]" title="Ricarica" data-testid="calendar-refresh-btn">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
          <button onClick={disconnect} className="p-2 border border-[#0A0A0A]/30 hover:border-red-500 hover:text-red-500" title="Scollega" data-testid="calendar-disconnect-btn">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div className="p-5 space-y-3">
        {showTplEditor && (
          <TemplateEditor
            templates={templates}
            onSave={(t) => { setTemplates(t); saveTemplates(t); setShowTplEditor(false); toast.success("Template salvati"); }}
            onClose={() => setShowTplEditor(false)}
          />
        )}
        {status?.connected && events.length > 0 && (
          <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-widest text-green-700 font-bold" title="15 minuti prima di ogni call ricevi un promemoria con azione WhatsApp">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-600"></span>
            </span>
            Auto-reminder pre-call attivo (15 min prima)
          </div>
        )}
        {scopeError && (
          <div className="border-2 border-red-500 bg-red-50 p-4 mb-2" data-testid="calendar-scope-error">
            <div className="font-display text-lg uppercase text-red-700 mb-2 flex items-center gap-2">
              <Ban size={18} /> Permesso Calendar mancante
            </div>
            <div className="text-sm text-red-800 mb-3 leading-relaxed">
              Google ha collegato l'account <b>{status.email}</b> ma <b>non</b> ha concesso l'accesso al calendario.
              Questo succede se la casella <b>"Visualizza calendari Google"</b> non era spuntata nella schermata di consenso.
            </div>
            <ol className="text-xs text-red-800 list-decimal list-inside space-y-1 mb-3">
              <li>Clicca <b>Scollega</b> qui sotto (revoca anche lato Google)</li>
              <li>Clicca <b>Collega Google Calendar</b></li>
              <li>Nella schermata Google <b>SPUNTA TUTTE le caselle</b> (soprattutto quella del Calendario)</li>
              <li>Clicca <b>Continua</b></li>
            </ol>
            <button
              onClick={disconnect}
              className="btn-cta bg-red-600 border-red-700 hover:bg-red-700 text-white text-xs"
              data-testid="calendar-scope-error-disconnect"
            >
              <Trash2 size={14} /> Scollega ora per ricollegare correttamente
            </button>
          </div>
        )}
        {loading && events.length === 0 && (
          <div className="text-sm text-[#0A0A0A]/50 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Carico eventi...</div>
        )}
        {!loading && events.length === 0 && (
          <div className="text-sm text-[#0A0A0A]/50 py-4 text-center">Nessun evento nel periodo selezionato</div>
        )}
        {events.map((ev) => {
          const evStart = new Date(ev.start);
          // Data locale ITA per l'header piccolo a sinistra
          const dayIT = ev.all_day ? "" : evStart.toLocaleString("it-IT", { day: "2-digit", month: "short", timeZone: "Europe/Rome" });
          // Ora italiana canonica (sempre in Europe/Rome)
          const timeItaly = ev.all_day ? "" : evStart.toLocaleString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });
          const timeStr = ev.all_day ? "Tutto il giorno" : `${dayIT} · 🇮🇹 ${timeItaly}`;
          const timeOnly = ev.all_day ? "" : timeItaly;
          // Data ISO per il conversion helper (giorno dell'evento in ora italiana)
          const evDateISO = ev.all_day ? (ev.start || "").slice(0, 10) : new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Rome" }).format(evStart);
          const artistName = cleanArtistName(ev.summary);
          // Resolve phone in modo sicuro: no fallback casuale sulla rubrica
          const phone = resolveEventPhone(ev, rubrica, rubricaByEmail, user?.email, artistName);
          const morningText = fillTemplate(templates.morning, { name: artistName, time: timeItaly, sender: senderName });
          const precallText = fillTemplate(templates.precall, { name: artistName, link: ev.hangout_link || ev.html_link || "", time: timeItaly, sender: senderName });
          const morningLink = buildWaLink(phone, morningText);
          const precallLink = buildWaLink(phone, precallText);
          return (
            <div key={ev.id} className="border-2 border-[#0A0A0A] bg-[#FAFAF7] p-4" data-testid={`calendar-event-${ev.id}`}>
              <div className="flex items-start gap-4">
                <div className="w-16 shrink-0 text-center">
                  <div className="font-display text-xl text-[#FF5A1F] leading-none">
                    {ev.all_day ? "🗓️" : evStart.getDate().toString().padStart(2, "0")}
                  </div>
                  <div className="text-[9px] uppercase tracking-widest text-[#0A0A0A]/50 mt-1">
                    {timeOnly && <>🇮🇹 {timeOnly}</>}
                  </div>
                  {!ev.all_day && !isBrowserInItaly() && (
                    <div className="text-[9px] tracking-tight text-[#0A0A0A]/40 mt-0.5 leading-tight">
                      tu {italianTimeToLocal(evDateISO, timeItaly)}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg uppercase text-[#0A0A0A] leading-tight">{ev.summary}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/50 mt-1">{timeStr}</div>
                  {ev.location && (
                    <div className="mt-1 text-xs text-[#0A0A0A]/70 flex items-center gap-1"><MapPin size={12} />{ev.location}</div>
                  )}
                  {ev.hangout_link && (
                    <a href={ev.hangout_link} target="_blank" rel="noreferrer" className="mt-1 text-xs text-[#FF5A1F] inline-flex items-center gap-1 hover:underline">
                      <Video size={12} /> Google Meet
                    </a>
                  )}
                  {ev.attendees.length > 0 && (
                    <div className="mt-1 text-[10px] text-[#0A0A0A]/50 truncate">{ev.attendees.slice(0, 3).join(", ")}{ev.attendees.length > 3 ? ` +${ev.attendees.length - 3}` : ""}</div>
                  )}
                </div>
                {ev.html_link && (
                  <a href={ev.html_link} target="_blank" rel="noreferrer" className="p-2 text-[#0A0A0A]/40 hover:text-[#FF5A1F]" title="Apri su Google Calendar">
                    <Link2 size={14} />
                  </a>
                )}
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm(`Nascondere "${ev.summary}" dall'agenda del CRM?\n\nL'evento resta su Google Calendar, ma non apparirà più qui.`)) return;
                    try {
                      await api.post("/mio-giorno/calendar/hide-event", { event_id: ev.id });
                      toast.success("Evento nascosto. Ricarica per aggiornare l'agenda.");
                      loadEvents();
                    } catch (e) {
                      toast.error("Errore nascondere evento: " + (e?.response?.data?.detail || e.message));
                    }
                  }}
                  className="p-2 text-[#0A0A0A]/40 hover:text-red-600"
                  title="Nascondi questo evento (es. hotel, voli, personali) — non viene eliminato da Google"
                  data-testid={`hide-event-${ev.id}`}
                >
                  <X size={14} />
                </button>
              </div>
              {/* Crea scheda CRM da questo evento */}
              <div className="mt-3 pt-3 border-t border-[#0A0A0A]/10 flex flex-wrap items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/50 mr-1">Scheda CRM:</span>
                {createdCards[ev.id] ? (
                  <span
                    className="text-[11px] font-bold uppercase tracking-wide text-green-700 bg-green-100 px-2.5 py-1 border border-green-700/30"
                    data-testid={`card-created-${ev.id}`}
                  >
                    ✓ Scheda creata
                    {createdCards[ev.id].inherited
                      ? ` · ${createdCards[ev.id].fieldsCount} dati ereditati`
                      : " · vuota (da compilare)"}
                  </span>
                ) : (
                  <button
                    type="button"
                    disabled={!!creatingCard[ev.id] || !artistName}
                    onClick={() => createCardFromEvent(ev, artistName, evDateISO, timeOnly, phone)}
                    className="text-[11px] font-bold uppercase tracking-wide bg-[#0A0A0A] text-[#FAFAF7] px-2.5 py-1 hover:bg-[#FF5A1F] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 transition-colors"
                    title={`Crea una scheda nel Mio Giorno per ${artistName}. Se è già cliente, eredita dati e note precedenti.`}
                    data-testid={`create-card-${ev.id}`}
                  >
                    {creatingCard[ev.id] ? (
                      <>
                        <Loader2 size={11} className="animate-spin" /> Creo…
                      </>
                    ) : (
                      <>
                        <PlusCircle size={11} /> Crea scheda
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* WhatsApp quick actions */}
              <div className="mt-3 pt-3 border-t border-[#0A0A0A]/10 flex flex-wrap items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/50 mr-1">WhatsApp:</span>
                {phone ? (
                  <>
                    <WaSendButton
                      link={morningLink}
                      label="Msg mattutino"
                      sent={!!sentMap[`${ev.id}::morning`]}
                      onMarkSent={() => markSent(ev.id, "morning")}
                      onUnmark={() => unmarkSent(ev.id, "morning")}
                      testId={`wa-morning-${ev.id}`}
                    />
                    <WaSendButton
                      link={precallLink}
                      label="Msg link call"
                      sent={!!sentMap[`${ev.id}::precall`]}
                      disabled={!ev.hangout_link}
                      disabledTitle="Nessun link Meet in questo evento"
                      onMarkSent={() => markSent(ev.id, "precall")}
                      onUnmark={() => unmarkSent(ev.id, "precall")}
                      testId={`wa-precall-${ev.id}`}
                    />
                    <span className="text-[10px] text-[#0A0A0A]/40 ml-auto" title="Numero risolto per questo evento">📞 {phone}</span>
                  </>
                ) : (
                  <span className="text-[10px] text-[#0A0A0A]/60 italic" title="Aggiungi il numero nella descrizione dell'evento Google Calendar (es. 'tel +39...') oppure crea/aggiorna il contatto nella Rubrica con la mail del partecipante">
                    ⚠️ Numero non identificato con certezza — inseriscilo nella descrizione dell'evento o nella Rubrica
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function TemplateEditor({ templates, onSave, onClose }) {
  const [morning, setMorning] = useState(templates.morning);
  const [precall, setPrecall] = useState(templates.precall);
  return (
    <div className="border-2 border-[#0A0A0A] bg-white p-4 mb-3" data-testid="wa-templates-editor">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display text-lg uppercase text-[#0A0A0A] flex items-center gap-2">
          <MessageCircle size={16} className="text-[#25D366]" /> Template WhatsApp
        </div>
        <button onClick={onClose} className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/60 hover:text-[#0A0A0A]" data-testid="wa-templates-close">
          Chiudi
        </button>
      </div>
      <div className="text-[10px] text-[#0A0A0A]/60 mb-3 leading-relaxed">
        Variabili disponibili: <code className="bg-[#F2F2EC] px-1">{"{name}"}</code> (nome artista) · <code className="bg-[#F2F2EC] px-1">{"{time}"}</code> (orario call in <b>ora italiana</b>, es. 15:00) · <code className="bg-[#F2F2EC] px-1">{"{link}"}</code> (link Google Meet) · <code className="bg-[#F2F2EC] px-1">{"{sender}"}</code> (il tuo nome — automatico dall'account)
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-[#FF5A1F] mb-1 block">
            🌅 Messaggio mattutino (giorno della call)
          </label>
          <textarea
            value={morning}
            onChange={(e) => setMorning(e.target.value)}
            rows={7}
            className="w-full border-2 border-[#0A0A0A] p-2 text-sm font-mono focus:outline-none focus:border-[#FF5A1F]"
            data-testid="wa-template-morning"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-[#FF5A1F] mb-1 block">
            🔗 Messaggio pre-call (T-15 minuti)
          </label>
          <textarea
            value={precall}
            onChange={(e) => setPrecall(e.target.value)}
            rows={7}
            className="w-full border-2 border-[#0A0A0A] p-2 text-sm font-mono focus:outline-none focus:border-[#FF5A1F]"
            data-testid="wa-template-precall"
          />
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 mt-3">
        <button
          onClick={() => { setMorning(DEFAULT_WA_TEMPLATES.morning); setPrecall(DEFAULT_WA_TEMPLATES.precall); }}
          className="text-xs uppercase tracking-widest text-[#0A0A0A]/60 hover:text-[#0A0A0A]"
          data-testid="wa-templates-reset"
        >
          Ripristina default
        </button>
        <button
          onClick={() => onSave({ morning, precall })}
          className="btn-cta text-xs"
          data-testid="wa-templates-save"
        >
          Salva template
        </button>
      </div>
    </div>
  );
}


function WaSendButton({ link, label, sent, onMarkSent, onUnmark, disabled, disabledTitle, testId }) {
  const handleClick = (e) => {
    if (disabled) { e.preventDefault(); return; }
    // Open WhatsApp and immediately mark as sent (user just needs to press Send in WA)
    if (!sent) onMarkSent();
  };
  if (sent) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 border-2 border-green-600 bg-green-600 text-white text-xs uppercase tracking-widest" data-testid={testId} title={label + " — inviato ✓"}>
        <CheckCircle2 size={12} /> {label}
        <button
          onClick={onUnmark}
          className="ml-1 text-white/70 hover:text-white text-[10px]"
          title="Annulla marcatura"
          data-testid={`${testId}-unmark`}
        >
          ✕
        </button>
      </span>
    );
  }
  return (
    <a
      href={disabled ? "#" : link}
      target={disabled ? undefined : "_blank"}
      rel="noreferrer"
      onClick={handleClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 border-2 text-xs uppercase tracking-widest transition-colors ${disabled ? "border-[#0A0A0A]/20 text-[#0A0A0A]/30 cursor-not-allowed" : "border-[#25D366] text-[#0A0A0A] hover:bg-[#25D366] hover:text-white"}`}
      title={disabled ? disabledTitle : ""}
      data-testid={testId}
    >
      <MessageCircle size={12} /> {label}
    </a>
  );
}

function BackupRestoreButton() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");   // "download" | "upload" | ""
  const fileRef = useRef(null);

  const downloadBackup = async () => {
    setBusy("download");
    try {
      const res = await api.get("/admin/backup/mio-giorno");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `perla_mio_giorno_backup_${ts}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup scaricato ✓");
    } catch (e) {
      toast.error("Errore backup: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy("");
    }
  };

  const uploadBackup = async (file) => {
    if (!file) return;
    const ok = window.confirm(
      "Ripristinare il backup?\n\nQuesto SOSTITUIRÀ i dati 'Il mio giorno' presenti in questo ambiente " +
      "(schede, rubrica, WhatsApp inviati, giorni caricati) con quelli del file selezionato.\n\n" +
      "Google Calendar verrà ricollegato con i tuoi token originali.\n\nContinuare?"
    );
    if (!ok) return;
    setBusy("upload");
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const res = await api.post("/admin/restore/mio-giorno", { payload, wipe_existing: true });
      const restored = res.data.restored || {};
      const summary = Object.entries(restored).map(([k, v]) => `${k}: ${v}`).join("\n");
      toast.success("Ripristino completato ✓");
      window.alert("Ripristino completato:\n\n" + summary + "\n\nRicarico la pagina...");
      window.location.reload();
    } catch (e) {
      toast.error("Errore ripristino: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy("");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-2 border-2 border-[#0A0A0A] text-xs uppercase tracking-widest hover:bg-[#0A0A0A] hover:text-[#FAFAF7]"
        title="Backup / Ripristina dati Il mio giorno"
        data-testid="backup-restore-btn"
      >
        <Database size={14} /> Backup
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="backup-restore-modal">
          <div className="bg-[#FAFAF7] border-4 border-[#0A0A0A] max-w-2xl w-full">
            <div className="flex items-center justify-between p-5 border-b-2 border-[#0A0A0A] bg-[#0A0A0A] text-[#FAFAF7]">
              <div className="font-display text-xl uppercase flex items-center gap-2">
                <Database size={18} /> Backup / Ripristina "Il mio giorno"
              </div>
              <button onClick={() => setOpen(false)} className="text-[#FAFAF7]/60 hover:text-[#FF5A1F]" data-testid="backup-modal-close">✕</button>
            </div>
            <div className="p-6 space-y-5">
              <div className="text-sm text-[#0A0A0A]/80 leading-relaxed">
                Esporta o importa <b>solo</b> i dati di "Il mio giorno": schede giornaliere, rubrica, marcatori
                WhatsApp, PDF caricati e collegamento Google Calendar. Utile <b>prima di fare il deploy in produzione</b>.
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={downloadBackup}
                  disabled={!!busy}
                  className="btn-cta disabled:opacity-60"
                  data-testid="backup-download-btn"
                >
                  {busy === "download" ? <Loader2 size={14} className="animate-spin" /> : <DownloadCloud size={14} />}
                  Scarica backup (.json)
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="application/json"
                  className="hidden"
                  onChange={(e) => uploadBackup(e.target.files?.[0])}
                  data-testid="backup-upload-input"
                />
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={!!busy}
                  className="px-4 py-2 border-2 border-[#0A0A0A] text-xs uppercase tracking-widest hover:bg-[#0A0A0A] hover:text-[#FAFAF7] disabled:opacity-60 flex items-center gap-2"
                  data-testid="backup-upload-btn"
                >
                  {busy === "upload" ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
                  Ripristina backup da file...
                </button>
              </div>
              <div className="border-l-4 border-[#FF5A1F] bg-[#FFF5EF] p-3 text-xs text-[#0A0A0A]/80 leading-relaxed">
                <b className="uppercase tracking-widest text-[#FF5A1F]">Flusso consigliato per la produzione</b>
                <ol className="list-decimal list-inside mt-1 space-y-0.5">
                  <li>Qui in preview clicca <b>"Scarica backup"</b></li>
                  <li>Fai il <b>Deploy</b> dalla piattaforma Emergent</li>
                  <li>Apri lo stesso pannello sul sito di produzione</li>
                  <li>Clicca <b>"Ripristina backup"</b> e seleziona il file scaricato</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// ---------------------------------------------------------------------------
// UnreadLeadsPill — contatore globale lead non letti (assegnati dal Setting)
// ---------------------------------------------------------------------------
function UnreadLeadsPill() {
  const [n, setN] = React.useState(0);
  const load = React.useCallback(async () => {
    try {
      const r = await api.get("/mio-giorno/unread-count");
      setN(r.data?.unread || 0);
    } catch { setN(0); }
  }, []);
  React.useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("mg-unread-refresh", h);
    window.addEventListener("mg-refresh", h);
    const t = setInterval(load, 60000);
    return () => { window.removeEventListener("mg-unread-refresh", h); window.removeEventListener("mg-refresh", h); clearInterval(t); };
  }, [load]);
  if (!n) return null;
  return (
    <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 border-2 border-[#FF5A1F] bg-[#FF5A1F]/10 text-[#FF5A1F] text-xs font-bold uppercase tracking-widest" data-testid="unread-leads-pill">
      🔥 {n} {n === 1 ? "lead nuovo per te" : "lead nuovi per te"} — scorri le date per trovarli
    </div>
  );
}
